import { useState, useRef, useEffect, useLayoutEffect, useCallback, forwardRef } from "react";
import { FaceLandmarker, ImageSegmenter, FilesetResolver } from "@mediapipe/tasks-vision";
import {
  ArrowLeft, ChevronDown, ChevronLeft, ChevronRight, ChevronUp, Copy, Download, FileJson,
  Image as ImageIcon, ImagePlus, RefreshCw, RotateCcw, ScanFace, SlidersHorizontal, Sparkles, Check, Clock, Smartphone,
  Cloud, AlertTriangle,
} from "lucide-react";
import CircleSequence from "./CircleSequence";
import QRCode from "qrcode";
import { generarToken, urlSubida, escucharFotoQR, descargarFotoQR } from "../../lib/fotosQR";
import {
  VARIANTES_NUBE, CALIDADES_NUBE, ESTIMADO, ARRANQUE_FRIO, nubeCaliente,
  recorteAjustado, quitarFondoGemini, generarEnNube, mensajeError,
} from "../../lib/stringartNube";

// ─── CONSTANTS ────────────────────────────────────────────────────────────────

const LEFT_EYE_IDX   = [362,382,381,380,374,373,390,249,263,466,388,387,386,385,384,398];
const RIGHT_EYE_IDX  = [33,7,163,144,145,153,154,155,133,173,157,158,159,160,161,246];
const NOSE_IDX       = [1,2,98,327,168,197,195,5,4,45,275,220,440,19,94];
const LIPS_IDX       = [61,185,40,39,37,0,267,269,270,409,291,375,321,405,314,17,84,181,91,146];
// Iris landmarks (FaceLandmarker returns 478 points total)
const IRIS_LEFT_IDX  = [468,469,470,471,472];
const IRIS_RIGHT_IDX = [473,474,475,476,477];

const CANVAS_SIZE = 720;
const RADIUS = 360;
const CX = 360;
const CY = 360;
const SA_SIZE_DEFAULT = 400;
const VARIANT_CANVAS_SIZE = 540;
const VARIANTS = [
  { type: "greedy",     weight: 5,   label: "5%"      },
  { type: "greedy",     weight: 7.5, label: "7.5%"    },
  { type: "greedy",     weight: 10,  label: "10%"     },
  // Familia distinta: en vez de buscar la línea más oscura sobre un residuo,
  // elige la que más reduce el error cuadrático entre el lienzo acumulado y la
  // foto, con líneas antialiasadas. Penaliza pasar hilo por donde ya hay.
  { type: "l2", mode: "nitido", weight: 8, label: "NÍTIDO" },
  { type: "l2", mode: "suave",  weight: 8, label: "SUAVE"  },
  // Igual que NÍTIDO pero realza internamente los bordes de la foto (máscara de
  // enfoque + más peso donde hay contorno) antes de trazar.
  { type: "l2", mode: "bordes", weight: 8, label: "BORDES" },
];

const IMPORTANCE_LEGEND = [
  { key: "importance_1", label: "Fondo / Cabello / Ropa", color: "#0a0d10", border: "#141a1f", imp: "×0.5" },
  { key: "importance_2", label: "Piel cuerpo",            color: "#0d2d4a", border: "#1a5080", imp: "×1"   },
  { key: "importance_3", label: "Piel rostro",            color: "#7a3510", border: "#d4601a", imp: "×2"   },
  { key: "importance_4", label: "Ojos · Nariz · Boca",   color: "#3d5000", border: "#a8d800", imp: "×3"   },
];

const PHASE_LABELS = ["01 · Foto", "02 · Encuadre", "03 · Resultado"];

// La máquina trabaja con 250 clavos; se admite hasta 200 pero es excepcional
// y la secuencia guardada queda marcada cuando no usa el valor estándar.
// Los índices de clavo siguen la convención de la app: el 0 queda a la derecha
// del círculo y aumentan en sentido horario. Debe coincidir con getPoint() de
// SequencePreview, o la secuencia se vería girada al usarla.
export const CLAVOS_MIN = 200;
export const CLAVOS_MAX = 250;
export const CLAVOS_ESTANDAR = 250;
const clampClavos = (n) => Math.max(CLAVOS_MIN, Math.min(CLAVOS_MAX, Math.round(Number(n) || CLAVOS_ESTANDAR)));

// ─── CANVAS UTILS ─────────────────────────────────────────────────────────────

function drawCircularImage(canvas, img, transform) {
  if (!canvas || !img) return;
  const ctx = canvas.getContext("2d");
  ctx.clearRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);
  ctx.save();
  ctx.beginPath();
  ctx.arc(CX, CY, RADIUS - 1, 0, Math.PI * 2);
  ctx.clip();
  const w = img.width * transform.zoom;
  const h = img.height * transform.zoom;
  ctx.save();
  ctx.translate(CX, CY);
  ctx.rotate((transform.rot * Math.PI) / 180);
  ctx.translate(transform.x, transform.y);
  ctx.drawImage(img, -w / 2, -h / 2, w, h);
  ctx.restore();
  ctx.restore();
  ctx.save();
  ctx.beginPath();
  ctx.arc(CX, CY, RADIUS - 1, 0, Math.PI * 2);
  ctx.strokeStyle = "rgba(59,130,246,0.55)";
  ctx.lineWidth = 2;
  ctx.stroke();
  ctx.restore();
}

function drawNails(canvas, count) {
  if (!canvas) return;
  const ctx = canvas.getContext("2d");
  ctx.clearRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);
  for (let i = 0; i < count; i++) {
    const angle = (2 * Math.PI * i) / count;
    const x = CX + (RADIUS - 3) * Math.cos(angle);
    const y = CY + (RADIUS - 3) * Math.sin(angle);
    ctx.beginPath();
    ctx.arc(x, y, count > 250 ? 2.7 : 4.2, 0, Math.PI * 2);
    ctx.fillStyle = "#3b82f6";
    ctx.fill();
  }
}

function getCircularBase64(img, transform, size = 400) {
  const off = document.createElement("canvas");
  off.width = size;
  off.height = size;
  const oc = off.getContext("2d");
  const scale = size / CANVAS_SIZE;
  oc.save();
  oc.beginPath();
  oc.arc(size / 2, size / 2, size / 2 - 1, 0, Math.PI * 2);
  oc.clip();
  const w = img.width * transform.zoom * scale;
  const h = img.height * transform.zoom * scale;
  oc.save();
  oc.translate(size / 2, size / 2);
  oc.rotate((transform.rot * Math.PI) / 180);
  oc.translate(transform.x * scale, transform.y * scale);
  oc.drawImage(img, -w / 2, -h / 2, w, h);
  oc.restore();
  oc.restore();
  return off.toDataURL("image/jpeg", 0.85);
}

// Convierte un desplazamiento en pantalla al sistema de coordenadas de la
// imagen (girado `rot` grados sobre el centro del círculo).
function desplazarEnPantalla(t, dx, dy) {
  const th = (t.rot * Math.PI) / 180;
  const c = Math.cos(th), s = Math.sin(th);
  return { ...t, x: t.x + dx * c + dy * s, y: t.y - dx * s + dy * c };
}

function fitZoom(img) {
  return (RADIUS * 2) / Math.min(img.width, img.height);
}

function renderResultCanvases(origB64, parsed, refs) {
  const { origRef, maskRef, overlayRef } = refs;
  const origImg = new Image();
  origImg.onload = () => {
    const ctxO = origRef.current.getContext("2d");
    ctxO.clearRect(0, 0, 200, 200);
    ctxO.save();
    ctxO.beginPath();
    ctxO.arc(100, 100, 99, 0, Math.PI * 2);
    ctxO.clip();
    ctxO.drawImage(origImg, 0, 0, 200, 200);
    ctxO.restore();

    const ctxM = maskRef.current.getContext("2d");
    ctxM.clearRect(0, 0, 200, 200);
    ctxM.save();
    ctxM.beginPath();
    ctxM.arc(100, 100, 99, 0, Math.PI * 2);
    ctxM.clip();
    // Background
    ctxM.fillStyle = "#0f1923";
    ctxM.fillRect(0, 0, 200, 200);
    // Hair/body base
    ctxM.fillStyle = "#0d2d4a";
    ctxM.fillRect(0, 0, 200, 200);
    // Face skin from segmentation
    if (parsed.segMask) {
      const { segMask, segMaskWidth: mW, segMaskHeight: mH } = parsed;
      const sz = 200;
      const segCanvas = document.createElement("canvas");
      segCanvas.width = sz; segCanvas.height = sz;
      const sCtx = segCanvas.getContext("2d");
      const imgData = sCtx.createImageData(sz, sz);
      const colors = {
        0: [15,25,35],    // bg → very dark (weight 1)
        1: [15,25,35],    // hair → same as bg (weight 1)
        2: [13,45,74],    // body skin → dark blue (weight 2)
        3: [122,53,16],   // face → orange-brown (weight 3)
        4: [15,25,35],    // clothes → same as bg (weight 1)
        5: [15,25,35],    // accessories → same as bg (weight 1)
      };
      for (let y = 0; y < sz; y++) {
        for (let x = 0; x < sz; x++) {
          const mx = Math.min(mW-1, Math.floor(x * mW / sz));
          const my = Math.min(mH-1, Math.floor(y * mH / sz));
          const cls = segMask[my * mW + mx];
          const c = colors[cls] || colors[0];
          const i = (y * sz + x) * 4;
          imgData.data[i]=c[0]; imgData.data[i+1]=c[1]; imgData.data[i+2]=c[2]; imgData.data[i+3]=255;
        }
      }
      sCtx.putImageData(imgData, 0, 0);
      ctxM.drawImage(segCanvas, 0, 0);
    }
    // Eye/nose/mouth overlay
    ["importance_4"].forEach((key) => {
      const region = parsed.regions?.[key];
      if (!region?.polygons) return;
      ctxM.fillStyle = region.color;
      region.polygons.forEach((poly) => {
        if (!poly || poly.length < 3) return;
        ctxM.beginPath();
        ctxM.moveTo(poly[0][0] * 200, poly[0][1] * 200);
        for (let i = 1; i < poly.length; i++) ctxM.lineTo(poly[i][0] * 200, poly[i][1] * 200);
        ctxM.closePath();
        ctxM.fill();
      });
    });
    ctxM.restore();

    const ctxV = overlayRef.current.getContext("2d");
    ctxV.clearRect(0, 0, 200, 200);
    ctxV.save();
    ctxV.beginPath();
    ctxV.arc(100, 100, 99, 0, Math.PI * 2);
    ctxV.clip();
    ctxV.drawImage(origImg, 0, 0, 200, 200);
    ctxV.globalAlpha = 0.52;
    ctxV.drawImage(maskRef.current, 0, 0, 200, 200);
    ctxV.globalAlpha = 1;
    ctxV.restore();
  };
  origImg.src = origB64;
}

// ─── IMPORTANCE MAP BUILDER ──────────────────────────────────────────────────

// Weights:
//   outside circle → 0
//   background / hair / clothes / accessories → 0.5
//   body skin (cls 2) → 1
//   face skin (cls 3) → 2
//   eyes / nose / mouth → 3
function buildImportanceMap(parsed, size, ignoreFeatures = false, weights = { bg: 0.5, hair: 1, body: 1, face: 2, features: 3 }) {
  const map = new Float32Array(size * size);
  const cx = size / 2, cy = size / 2, r = size / 2 - 1;

  // Step 1: fill with weights based on segmentation (pixel by pixel)
  if (parsed.segMask) {
    const { segMask, segMaskWidth: mW, segMaskHeight: mH } = parsed;
    for (let y = 0; y < size; y++) {
      for (let x = 0; x < size; x++) {
        const dx = x - cx, dy = y - cy;
        if (dx * dx + dy * dy > r * r) { map[y * size + x] = 0; continue; }
        const mx = Math.min(mW - 1, Math.floor(x * mW / size));
        const my = Math.min(mH - 1, Math.floor(y * mH / size));
        const cls = segMask[my * mW + mx];
        if (cls === 3)      map[y * size + x] = weights.face;
        else if (cls === 2) map[y * size + x] = weights.body;
        else if (cls === 1) map[y * size + x] = weights.hair;
        else                map[y * size + x] = weights.bg;
      }
    }
  } else {
    // Fallback: no segmentation — fill circle with 1
    for (let y = 0; y < size; y++) {
      for (let x = 0; x < size; x++) {
        const dx = x - cx, dy = y - cy;
        map[y * size + x] = (dx * dx + dy * dy <= r * r) ? 1 : 0;
      }
    }
  }

  // Step 2: overlay facial feature polygons in layers (each overrides the previous)
  // Order matters: nose/mouth → eye oval → eye white → iris
  const featureLayers = ignoreFeatures
    ? []
    : [
        { regions: ["importance_4"],                        weight: weights.nose_mouth, eyeOnly: false },
        { regions: ["importance_4"],                        weight: weights.eye_oval,   eyeOnly: true  },
        { regions: ["eye_white"],                           weight: weights.eye_white,  eyeOnly: false },
        { regions: ["iris"],                                weight: weights.iris,       eyeOnly: false },
      ];

  // Separate eye polygons from nose/mouth in importance_4
  const eyeKeys  = new Set(["eye_white", "iris"]);
  const eyePolys = [
    ...(parsed.regions?.importance_4?.polygons?.slice(0, (parsed.regions?.importance_4?.polygons?.length || 0) - 2) ?? []),
    // eye polygons are the first entries (left+right eye); nose and mouth are appended after
  ];
  // Re-derive: eyes are first 2*(numFaces) entries, then nose*(numFaces), then lips*(numFaces)
  const allI4 = parsed.regions?.importance_4?.polygons ?? [];
  const fc = parsed.faceCount || 1;
  const eyeI4   = allI4.slice(0, fc * 2);           // 2 eye polys per face
  const restI4  = allI4.slice(fc * 2);              // nose + lips

  const rasterize = (polys) => {
    const off = document.createElement("canvas");
    off.width = size; off.height = size;
    const ctx = off.getContext("2d");
    ctx.fillStyle = "#ffffff";
    polys.forEach((poly) => {
      if (!poly || poly.length < 3) return;
      ctx.beginPath();
      ctx.moveTo(poly[0][0] * size, poly[0][1] * size);
      for (let k = 1; k < poly.length; k++) ctx.lineTo(poly[k][0] * size, poly[k][1] * size);
      ctx.closePath();
      ctx.fill();
    });
    return ctx.getImageData(0, 0, size, size).data;
  };

  // Nose + mouth
  if (!ignoreFeatures && restI4.length) {
    const px = rasterize(restI4);
    for (let i = 0; i < size * size; i++)
      if (px[i * 4] > 0 && map[i] > 0) map[i] = weights.nose_mouth;
  }
  // Eye oval (eyelid border)
  if (!ignoreFeatures && eyeI4.length) {
    const px = rasterize(eyeI4);
    for (let i = 0; i < size * size; i++)
      if (px[i * 4] > 0 && map[i] > 0) map[i] = weights.eye_oval;
  }
  // Sclera (low weight → algorithm avoids → stays white)
  const ewPolys = parsed.regions?.eye_white?.polygons ?? [];
  if (!ignoreFeatures && ewPolys.length) {
    const px = rasterize(ewPolys);
    for (let i = 0; i < size * size; i++)
      if (px[i * 4] > 0 && map[i] > 0) map[i] = weights.eye_white;
  }
  // Iris (highest weight → darkest)
  const irisPolys = parsed.regions?.iris?.polygons ?? [];
  if (!ignoreFeatures && irisPolys.length) {
    const px = rasterize(irisPolys);
    for (let i = 0; i < size * size; i++)
      if (px[i * 4] > 0 && map[i] > 0) map[i] = weights.iris;
  }

  return map;
}

function getGrayscaleData(b64, size) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const off = document.createElement("canvas");
      off.width = size;
      off.height = size;
      const ctx = off.getContext("2d");
      ctx.drawImage(img, 0, 0, size, size);
      const data = ctx.getImageData(0, 0, size, size).data;
      const raw = new Float32Array(size * size);
      for (let i = 0; i < size * size; i++) {
        raw[i] = 1 - (data[i * 4] * 0.299 + data[i * 4 + 1] * 0.587 + data[i * 4 + 2] * 0.114) / 255;
      }
      resolve(raw);
    };
    img.src = b64;
  });
}


// ─── STRING ART WEB WORKER ───────────────────────────────────────────────────

const WORKER_CODE = `
self.onmessage = function(e) {
  var d = e.data;
  var gray = new Float32Array(d.gray);
  var importance = new Float32Array(d.importance);
  var W = d.width;
  var nails = d.nailCount;
  var maxLines = d.maxLines;
  var sw = d.stringWeight;
  var minDist = d.minDistance;

  // Nail positions
  var nailX = new Float32Array(nails);
  var nailY = new Float32Array(nails);
  var r = W / 2 - 1;
  for (var i = 0; i < nails; i++) {
    var angle = (2 * Math.PI * i) / nails;
    nailX[i] = W / 2 + r * Math.cos(angle);
    nailY[i] = W / 2 + r * Math.sin(angle);
  }

  // Precompute all line pixel indices using Bresenham
  function getLinePixels(x0, y0, x1, y1) {
    x0 = Math.round(x0); y0 = Math.round(y0);
    x1 = Math.round(x1); y1 = Math.round(y1);
    var pixels = [];
    var dx = Math.abs(x1 - x0), dy = Math.abs(y1 - y0);
    var sx = x0 < x1 ? 1 : -1, sy = y0 < y1 ? 1 : -1;
    var err = dx - dy;
    while (true) {
      if (x0 >= 0 && x0 < W && y0 >= 0 && y0 < W) pixels.push(y0 * W + x0);
      if (x0 === x1 && y0 === y1) break;
      var e2 = 2 * err;
      if (e2 > -dy) { err -= dy; x0 += sx; }
      if (e2 < dx) { err += dx; y0 += sy; }
    }
    return pixels;
  }

  // Greedy algorithm: iteratively pick the line with highest weighted darkness
  var sequence = [];
  var currentNail = 0;
  sequence.push(0);
  var lastNail = -1; // avoid going back to previous nail

  for (var iter = 0; iter < maxLines; iter++) {
    var bestNail = -1;
    var bestScore = -Infinity;

    for (var c = 0; c < nails; c++) {
      if (c === currentNail || c === lastNail) continue;
      var dist = Math.abs(c - currentNail);
      if (dist > nails / 2) dist = nails - dist;
      if (dist < minDist) continue;

      // Score: average weighted darkness along line
      var pixels = getLinePixels(nailX[currentNail], nailY[currentNail], nailX[c], nailY[c]);
      var score = 0;
      for (var p = 0; p < pixels.length; p++) {
        score += gray[pixels[p]] * importance[pixels[p]];
      }
      if (pixels.length > 0) score /= pixels.length;

      if (score > bestScore) {
        bestScore = score;
        bestNail = c;
      }
    }

    if (bestNail === -1 || bestScore <= 0.005) break;

    // Subtract string from working image (reduce darkness where string passes)
    var drawn = getLinePixels(nailX[currentNail], nailY[currentNail], nailX[bestNail], nailY[bestNail]);
    for (var p = 0; p < drawn.length; p++) {
      gray[drawn[p]] = Math.max(0, gray[drawn[p]] - sw);
    }

    sequence.push(bestNail);
    lastNail = currentNail;
    currentNail = bestNail;

    if (iter % 50 === 0) {
      self.postMessage({ type: "progress", line: iter, total: maxLines });
    }
  }

  self.postMessage({ type: "done", sequence: sequence, totalLines: sequence.length - 1 });
};
`;

function createStringWorker() {
  const blob = new Blob([WORKER_CODE], { type: "application/javascript" });
  return new Worker(URL.createObjectURL(blob));
}

// ─── L2 (ERROR CUADRÁTICO) WORKER ────────────────────────────────────────────
// Mantiene un lienzo C con el hilo acumulado y, en cada paso, elige la línea que
// más reduce  Σ w·(T − C)²  donde T es la oscuridad objetivo. La ganancia de una
// línea con cobertura a_p es  Σ w·a·(2(T − C) − a): negativa si ya hay hilo de
// sobra, así que también decide sola cuándo parar. Las líneas se rasterizan con
// antialiasing (Xiaolin Wu) y, en modo "suave", con un núcleo de 3 px para que
// el error se evalúe sobre tonos y no píxel a píxel.

const L2_WORKER_CODE = `
self.onmessage = function(e) {
  var d = e.data;
  var T = new Float32Array(d.gray);
  var Wt = new Float32Array(d.importance);
  var W = d.width;
  var nails = d.nailCount;
  var maxLines = d.maxLines;
  var sw = d.stringWeight;
  var minDist = d.minDistance;
  var suave  = d.mode === "suave";
  var bordes = d.mode === "bordes";

  function blur3(src) {
    var out = new Float32Array(W * W);
    for (var y = 0; y < W; y++) for (var x = 0; x < W; x++) {
      var acc = 0, cnt = 0;
      for (var oy = -1; oy <= 1; oy++) for (var ox = -1; ox <= 1; ox++) {
        var yy = y + oy, xx = x + ox;
        if (yy < 0 || yy >= W || xx < 0 || xx >= W) continue;
        acc += src[yy * W + xx]; cnt++;
      }
      out[y * W + x] = acc / cnt;
    }
    return out;
  }

  // Modo suave: objetivo con desenfoque de caja 3x3 (tonos, no detalle).
  if (suave) T = blur3(T);

  // Modo bordes: dos realces internos, sin parámetros para el usuario.
  //  1) Máscara de enfoque: T' = T + k·(T − blur(T)). El lado oscuro de cada
  //     contorno se oscurece y el claro se aclara, así el hilo "dibuja" el borde.
  //  2) Más peso donde hay contorno (magnitud Sobel normalizada), para que el
  //     error en los bordes cuente más que el de las zonas planas.
  if (bordes) {
    var Tb2 = blur3(T);
    var Ts = new Float32Array(W * W);
    var K_ENFOQUE = 0.7;
    for (var i0 = 0; i0 < W * W; i0++) {
      var v = T[i0] + K_ENFOQUE * (T[i0] - Tb2[i0]);
      Ts[i0] = v < 0 ? 0 : (v > 1 ? 1 : v);
    }
    var edge = new Float32Array(W * W), emax = 1e-6;
    for (var y2 = 1; y2 < W - 1; y2++) for (var x2 = 1; x2 < W - 1; x2++) {
      var o = y2 * W + x2;
      var gx = -Tb2[o - W - 1] + Tb2[o - W + 1] - 2 * Tb2[o - 1] + 2 * Tb2[o + 1] - Tb2[o + W - 1] + Tb2[o + W + 1];
      var gy = -Tb2[o - W - 1] - 2 * Tb2[o - W] - Tb2[o - W + 1] + Tb2[o + W - 1] + 2 * Tb2[o + W] + Tb2[o + W + 1];
      var mg = Math.sqrt(gx * gx + gy * gy);
      edge[o] = mg; if (mg > emax) emax = mg;
    }
    var K_PESO = 2.0;
    var Wt2 = new Float32Array(W * W);
    for (var i1 = 0; i1 < W * W; i1++) Wt2[i1] = Wt[i1] * (1 + K_PESO * Math.min(1, edge[i1] / (0.6 * emax)));
    T = Ts; Wt = Wt2;
  }

  var C = new Float32Array(W * W);   // hilo acumulado
  var halfW = W / 2, r = halfW - 1;
  var nailX = new Float32Array(nails), nailY = new Float32Array(nails);
  for (var i = 0; i < nails; i++) {
    var ang = (2 * Math.PI * i) / nails;
    nailX[i] = halfW + r * Math.cos(ang);
    nailY[i] = halfW + r * Math.sin(ang);
  }

  // Rasterizado Wu: (índice, cobertura) en buffers reutilizables.
  var cap = W * 8;
  var idxBuf = new Int32Array(cap);
  var covBuf = new Float32Array(cap);
  var n = 0;
  function plot(x, y, c) {
    if (c <= 0.001 || x < 0 || y < 0 || x >= W || y >= W || n >= cap) return;
    idxBuf[n] = y * W + x; covBuf[n] = c; n++;
  }
  // En modo suave cada muestra se reparte con el núcleo [.25 .5 .25] en el
  // eje perpendicular: equivale a desenfocar la línea, y el desenfoque es
  // lineal, así que blur(C + linea) = blur(C) + blur(linea).
  function plotK(x, y, c, steep) {
    if (!suave) { plot(x, y, c); return; }
    plot(x, y, c * 0.5);
    if (steep) { plot(x - 1, y, c * 0.25); plot(x + 1, y, c * 0.25); }
    else       { plot(x, y - 1, c * 0.25); plot(x, y + 1, c * 0.25); }
  }
  function wuLine(x0, y0, x1, y1) {
    n = 0;
    var steep = Math.abs(y1 - y0) > Math.abs(x1 - x0), t;
    if (steep) { t = x0; x0 = y0; y0 = t; t = x1; x1 = y1; y1 = t; }
    if (x0 > x1) { t = x0; x0 = x1; x1 = t; t = y0; y0 = y1; y1 = t; }
    var dx = x1 - x0, dy = y1 - y0;
    var grad = dx === 0 ? 1 : dy / dx;
    var xs = Math.round(x0), xe = Math.round(x1);
    var yInt = y0 + grad * (xs - x0);
    for (var x = xs; x <= xe; x++) {
      var yf = Math.floor(yInt), f = yInt - yf;
      if (steep) { plotK(yf, x, 1 - f, true);  plotK(yf + 1, x, f, true); }
      else       { plotK(x, yf, 1 - f, false); plotK(x, yf + 1, f, false); }
      yInt += grad;
    }
    return n;
  }

  function validPair(a, b) {
    if (a === b) return false;
    var dist = Math.abs(a - b);
    if (dist > nails / 2) dist = nails - dist;
    return dist >= minDist;
  }

  var sequence = [0];
  var cur = 0, last = -1;
  for (var iter = 0; iter < maxLines; iter++) {
    var best = -1, bestGain = 0;
    for (var c = 0; c < nails; c++) {
      if (c === cur || c === last || !validPair(cur, c)) continue;
      var m = wuLine(nailX[cur], nailY[cur], nailX[c], nailY[c]);
      var gain = 0;
      for (var p = 0; p < m; p++) {
        var i2 = idxBuf[p], a = sw * covBuf[p];
        gain += Wt[i2] * a * (2 * (T[i2] - C[i2]) - a);
      }
      if (gain > bestGain) { bestGain = gain; best = c; }
    }
    // Ninguna línea reduce el error: el dibujo está terminado.
    if (best === -1) break;
    var m2 = wuLine(nailX[cur], nailY[cur], nailX[best], nailY[best]);
    for (var q = 0; q < m2; q++) C[idxBuf[q]] += sw * covBuf[q];
    sequence.push(best);
    last = cur; cur = best;
    if (iter % 50 === 0) self.postMessage({ type: "progress", line: iter, total: maxLines });
  }
  self.postMessage({ type: "done", sequence: sequence, totalLines: sequence.length - 1 });
};
`;

function createL2Worker() {
  const blob = new Blob([L2_WORKER_CODE], { type: "application/javascript" });
  return new Worker(URL.createObjectURL(blob));
}

// ─── LOG COLORS ───────────────────────────────────────────────────────────────
const LOG_COLOR = {
  ok:    "#34d399",  // emerald-400
  err:   "#f87171",  // red-400
  info:  "#60a5fa",  // blue-400
  warn:  "#fbbf24",  // amber-400
  muted: "#64748b",  // slate-500
};

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────

// modo "clasico": variantes calculadas en el navegador (todos los usuarios).
// modo "nube": solo administradores; la secuencia la calcula la Cloud Function
// generarStringArt y dos variantes pasan antes por Gemini para limpiar el fondo.
export default function StringArtStudio({ onUsarSecuencia, onCancel, clavosIniciales = CLAVOS_ESTANDAR, modo = "clasico" }) {
  const esNube = modo === "nube";
  const [img, setImg] = useState(null);
  const [transform, setTransform] = useState({ x: 0, y: 0, zoom: 1, rot: 0 });
  const [nailCount, setNailCountRaw] = useState(() => clampClavos(clavosIniciales));
  const setNailCount = (n) => setNailCountRaw(clampClavos(n));
  // Panel de parámetros en la pantalla de resultado (cerrado por defecto).
  const [ajustesAbiertos, setAjustesAbiertos] = useState(false);
  // "Usar esta secuencia" guarda de verdad, así que puede tardar o fallar.
  const [guardando, setGuardando] = useState(false);
  const [errorUso, setErrorUso] = useState("");
  // La secuencia se puede usar en cuanto la variante elegida está lista, sin
  // esperar al resto; antes de guardar se pide un nombre para reconocerla.
  const [pidiendoNombre, setPidiendoNombre] = useState(false);
  const [nombreSecuencia, setNombreSecuencia] = useState("");
  const [logs, setLogs] = useState([
    { type: "info",  msg: "Sistema listo" },
    { type: "muted", msg: "Esperando imagen..." },
  ]);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  // Phase 3 state
  const [maxLines, setMaxLines] = useState(3000);
  const [ignoreFeatures, setIgnoreFeatures] = useState(false);
  const [weights, setWeights] = useState({ bg: 0.5, hair: 1, body: 1.1, face: 1.1, nose_mouth: 1.3, eye_oval: 1, eye_white: 1, iris: 1.5 });
  const setW = (key) => (val) => setWeights(w => ({ ...w, [key]: val }));
  const MIN_NAIL_DIST = 10;
  const [generating, setGenerating] = useState(false);
  const [variantProgress, setVariantProgress] = useState(() => VARIANTS.map(() => 0));
  const [stringVariants, setStringVariants] = useState(() => VARIANTS.map(() => null));
  const [selectedVariant, setSelectedVariant] = useState(1);
  const [showStringArt, setShowStringArt] = useState(false);
  const [lineThickness, setLineThickness] = useState(24); // ÷100 → real px
  const [posterization, setPosterization] = useState(32);
  const [overwhite, setOverwhite] = useState(100);
  const [saSize, setSaSize] = useState(SA_SIZE_DEFAULT);
  const [frameDiameter, setFrameDiameter] = useState(450);
  const [threadThickness, setThreadThickness] = useState(0.17);

  // ── Modo nube ──
  const [ajusteFoto, setAjusteFoto] = useState({ brillo: 100, contraste: 100 });
  const [calidadNube, setCalidadNube] = useState("ambas");
  // Una entrada por variante: { etapas, etapa, inicio, est, entrada, normal, maxima, error }
  const [nube, setNube] = useState(() => VARIANTES_NUBE.map(() => null));
  // "mejor" muestra la máxima en cuanto llega; "normal" fuerza la normal.
  const [vistaCalidad, setVistaCalidad] = useState("mejor");
  const [ahora, setAhora] = useState(() => Date.now());
  const corridaNubeRef = useRef(0);
  const generandoNube = nube.some((v) => v && v.etapa !== "listo" && v.etapa !== "error");

  const mainCanvasRef      = useRef(null);
  const nailCanvasRef      = useRef(null);
  const origRef            = useRef(null);
  const maskRef            = useRef(null);
  const overlayRef         = useRef(null);
  const logRef             = useRef(null);
  const dragging           = useRef(false);
  const lastMouse          = useRef({ x: 0, y: 0 });
  const faceLandmarkerRef  = useRef(null);
  const imageSegmenterRef  = useRef(null);
  const workersRef         = useRef([]);
  const [modelReady, setModelReady] = useState(false);

  // ── Medida del contenedor: todo el layout se deriva del ancho disponible ──
  const rootRef = useRef(null);
  const headerRef = useRef(null);
  const [contentW, setContentW] = useState(480);
  const [contentH, setContentH] = useState(720);
  const [altoCabecera, setAltoCabecera] = useState(64);
  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    const medir = () => {
      const r = el.getBoundingClientRect();
      setContentW(r.width || 480);
      setContentH(r.height || 720);
      setAltoCabecera(headerRef.current?.offsetHeight || 64);
    };
    medir();
    // El observador cubre los cambios del panel (abrir/cerrar la barra lateral);
    // el evento de ventana cubre girar el móvil o redimensionar, y sirve de
    // respaldo cuando el observador no entrega callbacks (pestaña sin pintar).
    window.addEventListener("resize", medir);
    window.addEventListener("orientationchange", medir);
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(medir) : null;
    ro?.observe(el);
    if (headerRef.current) ro?.observe(headerRef.current);
    return () => {
      window.removeEventListener("resize", medir);
      window.removeEventListener("orientationchange", medir);
      ro?.disconnect();
    };
  }, []);

  const movil     = contentW < 480;   // teléfono
  const compacto  = contentW < 900;   // teléfono o tablet vertical
  const bajo      = contentH < 560;   // teléfono apaisado o panel muy bajo
  const avail     = Math.max(200, contentW - (movil ? 24 : 48));
  const dialPad   = (movil || bajo) ? 30 : 44;  // aro de rotación alrededor del lienzo
  const cardSize  = Math.min(195, Math.max(120, (avail - 32) / (compacto ? 2 : 3)));

  // Alto que ocupan cabecera, textos, botones y separaciones en cada paso; lo
  // que sobra es para el lienzo o el disco, así el paso cabe sin scroll
  // siempre que el mínimo lo permita. Si no cabe, el panel hace scroll.
  const relleno   = movil ? 40 : 56;                 // padding vertical de main
  const gap       = 18;
  const altoTitulo = bajo ? 30 : (movil ? 62 : 46);  // número + título + descripción
  const altoAyuda  = bajo ? 0 : 22;
  const reservaFoto = altoCabecera + relleno + (bajo ? 0 : 44 + gap);

  // Con ancho de sobra, título y controles van en una columna junto al lienzo:
  // así el círculo puede usar todo el alto en vez de compartirlo con ellos.
  const lateral    = !compacto;
  const anchoPanel = 260;
  const anchoLienzo = avail - (lateral ? anchoPanel + 24 : 0);
  const reservaEncuadre = lateral
    ? altoCabecera + relleno
    : altoCabecera + relleno + altoTitulo + (movil ? 92 : 42) + 46 + altoAyuda + gap * 4;
  const reservaResultado = lateral
    ? altoCabecera + relleno + 44 + gap * 2
    : altoCabecera + relleno + altoTitulo + 44 + 46 + 18 + 42 + gap * 5;

  // Las reservas son una estimación: tras pintar se mide el exceso real de
  // main y se descuenta también, de modo que el paso encaje justo. Con los
  // ajustes avanzados abiertos el scroll es legítimo y no se corrige.
  const mainRef = useRef(null);
  const [correccion, setCorreccion] = useState({ clave: "", px: 0 });
  const faseActual = !img ? 0 : showStringArt ? 2 : 1;
  const claveAjuste = `${faseActual}|${Math.round(contentW)}|${Math.round(contentH)}|${altoCabecera}|${ajustesAbiertos}`;
  useLayoutEffect(() => {
    const m = mainRef.current;
    if (!m || correccion.clave === claveAjuste) return;
    const exceso = ajustesAbiertos ? 0 : Math.max(0, m.scrollHeight - m.clientHeight);
    setCorreccion({ clave: claveAjuste, px: exceso });
  }, [claveAjuste, correccion.clave, ajustesAbiertos]);
  const exceso = correccion.clave === claveAjuste ? correccion.px : 0;

  const uploadSize = Math.max(160, Math.min(340, avail, contentH - reservaFoto - exceso));
  const qrSize     = compacto ? 150 : Math.max(140, Math.min(190, uploadSize * 0.6));
  const canvasDisp = Math.max(200, Math.min(CANVAS_SIZE, anchoLienzo - dialPad * 2, contentH - reservaEncuadre - dialPad * 2 - exceso));
  const canvasScale = canvasDisp / CANVAS_SIZE;
  const wrapSize  = canvasDisp + dialPad * 2;
  // El disco es SVG, así que crece sin perder nitidez: se limita solo por el
  // hueco real, no por la resolución del trazado.
  const variantDisp = Math.max(
    200,
    Math.min(
      900,
      avail - 96 - (compacto ? 0 : 168) - (lateral ? anchoPanel + 24 : 0),
      contentH - reservaResultado - exceso
    )
  );

  // Los gestos llegan en px de pantalla; el lienzo trabaja en px lógicos.
  const scaleRef = useRef(1);
  scaleRef.current = canvasScale;

  useEffect(() => {
    async function initModel() {
      addLog("info", "Cargando modelos MediaPipe...");
      try {
        const vision = await FilesetResolver.forVisionTasks(
          "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@latest/wasm"
        );
        faceLandmarkerRef.current = await FaceLandmarker.createFromOptions(vision, {
          baseOptions: {
            modelAssetPath: "https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/1/face_landmarker.task",
            delegate: "GPU",
          },
          runningMode: "IMAGE",
          numFaces: 4,
        });
        addLog("ok", "FaceLandmarker listo ✓");
        imageSegmenterRef.current = await ImageSegmenter.createFromOptions(vision, {
          baseOptions: {
            modelAssetPath: "https://storage.googleapis.com/mediapipe-models/image_segmenter/selfie_multiclass_256x256/float32/latest/selfie_multiclass_256x256.tflite",
            delegate: "GPU",
          },
          runningMode: "IMAGE",
          outputCategoryMask: true,
          outputConfidenceMasks: false,
        });
        setModelReady(true);
        addLog("ok", "Segmentador listo ✓");
      } catch (e) {
        addLog("err", "Error cargando modelos: " + e.message);
      }
    }
    initModel();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // `lateral` y `showStringArt` cambian la estructura del JSX, así que React
  // remonta los <canvas> y salen en blanco: hay que repintarlos al volver.
  useEffect(() => {
    if (img) drawCircularImage(mainCanvasRef.current, img, transform);
  }, [img, transform, lateral, showStringArt]);

  useEffect(() => {
    drawNails(nailCanvasRef.current, nailCount);
  }, [nailCount, img, lateral, showStringArt]);

  useEffect(() => {
    if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight;
  }, [logs]);


  // Reloj del modo nube: refresca el tiempo estimado mientras haya peticiones.
  useEffect(() => {
    if (!generandoNube) return;
    const id = setInterval(() => setAhora(Date.now()), 500);
    return () => clearInterval(id);
  }, [generandoNube]);

  // Al salir se ignoran las respuestas que sigan en camino.
  useEffect(() => () => { corridaNubeRef.current++; }, []);

  // Cleanup workers on unmount
  useEffect(() => {
    return () => { workersRef.current.forEach(w => w?.terminate()); };
  }, []);

  // Al cargar una foto se centra el rostro solo, sin que el usuario tenga que
  // pedirlo; si el modelo aún carga, se hace en cuanto esté listo.
  const autoEncuadradaRef = useRef(null);
  useEffect(() => {
    if (!img || !modelReady || autoEncuadradaRef.current === img) return;
    handleAutoFrame();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [img, modelReady]);

  // Las tarjetas del mapa de importancias solo existen con el panel abierto.
  useEffect(() => {
    if (!ajustesAbiertos || !result) return;
    const t = setTimeout(() => renderResultCanvases(result.b64, result.parsed, { origRef, maskRef, overlayRef }), 60);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ajustesAbiertos, result]);

  const addLog = useCallback((type, msg) => {
    setLogs((prev) => [...prev, { type, msg }]);
  }, []);

  // ── Foto desde el teléfono (QR) ──
  // Cada apertura del generador crea un token nuevo; el QR lleva a
  // /subir/<token>, una página pública desde la que el teléfono envía la foto.
  // La escucha vive mientras el generador esté abierto, así que una foto
  // enviada en cualquier paso sustituye a la actual.
  const [tokenQR] = useState(generarToken);
  const [qrDataUrl, setQrDataUrl] = useState(null);
  const [recibiendoQR, setRecibiendoQR] = useState(false);
  const handleFileRef = useRef(null);
  useEffect(() => {
    let vivo = true;
    QRCode.toDataURL(urlSubida(tokenQR), { margin: 1, width: 512, errorCorrectionLevel: "M",
      color: { dark: "#0f172a", light: "#ffffff" } })
      .then((url) => { if (vivo) setQrDataUrl(url); })
      .catch((err) => console.error("No se pudo generar el QR:", err));
    return () => { vivo = false; };
  }, [tokenQR]);
  useEffect(() => {
    const parar = escucharFotoQR(tokenQR, async (foto) => {
      setRecibiendoQR(true);
      addLog("info", "Recibiendo foto del teléfono…");
      try {
        const file = await descargarFotoQR(foto);
        handleFileRef.current?.(file);
      } catch (err) {
        console.error(err);
        addLog("error", "No se pudo descargar la foto del teléfono");
      } finally {
        setRecibiendoQR(false);
      }
    }, () => addLog("error", "Sin conexión con el envío por QR"));
    return parar;
  }, [tokenQR, addLog]);

  // ── File ──
  const handleFile = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const image = new Image();
      image.onload = () => {
        const zoom = fitZoom(image);
        setImg(image);
        setTransform({ x: 0, y: 0, zoom, rot: 0 });
        setShowStringArt(false);
        setResult(null);
        setStringVariants(VARIANTS.map(() => null));
        corridaNubeRef.current++;
        setNube(VARIANTES_NUBE.map(() => null));
        addLog("ok", `Imagen: ${file.name}`);
        addLog("muted", `${image.width}×${image.height}px`);
      };
      image.src = ev.target.result;
    };
    reader.readAsDataURL(file);
  };
  handleFileRef.current = handleFile;

  // ── Drag ──
  const onMouseDown = (e) => {
    if (!img) return;
    dragging.current = true;
    lastMouse.current = { x: e.clientX, y: e.clientY };
  };
  const onMouseMove = useCallback((e) => {
    if (!dragging.current || !img) return;
    const k = scaleRef.current || 1;
    const dx = (e.clientX - lastMouse.current.x) / k;
    const dy = (e.clientY - lastMouse.current.y) / k;
    lastMouse.current = { x: e.clientX, y: e.clientY };
    setTransform((t) => desplazarEnPantalla(t, dx, dy));
  }, [img]);
  const onMouseUp = () => { dragging.current = false; };

  useEffect(() => {
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };
  }, [onMouseMove]);

  const onWheel = (e) => {
    if (!img) return;
    e.preventDefault();
    const factor = e.deltaY < 0 ? 1.06 : 0.94;
    setTransform((t) => ({ ...t, zoom: Math.max(0.1, Math.min(6, t.zoom * factor)) }));
  };

  // ── Auto-encuadre facial ──
  const handleAutoFrame = async () => {
    if (!img) return;
    if (!faceLandmarkerRef.current) { addLog("warn", "Modelo aún cargando..."); return; }
    autoEncuadradaRef.current = img;
    addLog("info", "Detectando rostro para auto-encuadre...");
    try {
      const detection = faceLandmarkerRef.current.detect(img);
      const allLandmarks = detection.faceLandmarks ?? [];
      if (allLandmarks.length === 0) {
        addLog("warn", "No se detectó rostro para encuadrar");
        return;
      }
      let minX = 1, minY = 1, maxX = 0, maxY = 0;
      for (const lm of allLandmarks) {
        for (const pt of lm) {
          if (pt.x < minX) minX = pt.x;
          if (pt.y < minY) minY = pt.y;
          if (pt.x > maxX) maxX = pt.x;
          if (pt.y > maxY) maxY = pt.y;
        }
      }
      const faceCx = ((minX + maxX) / 2) * img.width;
      const faceCy = ((minY + maxY) / 2) * img.height;
      const faceW = (maxX - minX) * img.width;
      const faceH = (maxY - minY) * img.height;
      const faceRadius = Math.max(faceW, faceH) / 2 * 1.4;
      const zoom = RADIUS / faceRadius;
      const x = zoom * (img.width / 2 - faceCx);
      const y = zoom * (img.height / 2 - faceCy);
      setTransform({ x, y, zoom, rot: 0 });
      addLog("ok", `Auto-encuadre: ${allLandmarks.length} rostro(s) centrado(s) ✓`);
    } catch (err) {
      addLog("err", "Error en auto-encuadre: " + err.message);
    }
  };

  // ── Generate Importance Map (Phase 1 → 2) ──
  const analizarImagen = async () => {
    if (!img || loading) return null;
    if (!faceLandmarkerRef.current) { addLog("warn", "Modelo aún cargando, espera..."); return null; }
    setLoading(true);
    addLog("info", "Extrayendo imagen circular...");
    const b64 = getCircularBase64(img, transform, saSize);
    addLog("warn", "Analizando zonas faciales...");

    try {
      const htmlImg = new Image();
      htmlImg.src = b64;
      await new Promise((res, rej) => { htmlImg.onload = res; htmlImg.onerror = rej; });

      // ── Segmentation (face skin vs hair vs body) ──
      let segMask = null, segMaskWidth = 0, segMaskHeight = 0;
      if (imageSegmenterRef.current) {
        addLog("info", "Segmentando piel facial...");
        const segResult = imageSegmenterRef.current.segment(htmlImg);
        const catMask = segResult.categoryMask;
        segMaskWidth  = catMask.width;
        segMaskHeight = catMask.height;
        segMask = catMask.getAsUint8Array().slice(); // 0=bg,1=hair,2=body,3=face,4=clothes,5=acc
        catMask.close();
      }

      // ── Face landmarks (eyes, nose, mouth, iris — all faces) ──
      const detection = faceLandmarkerRef.current.detect(htmlImg);
      const allLandmarks = detection.faceLandmarks ?? [];
      const hasFace = allLandmarks.length > 0;

      const expandPoly = (poly, factor) => {
        const cx = poly.reduce((s, p) => s + p[0], 0) / poly.length;
        const cy = poly.reduce((s, p) => s + p[1], 0) / poly.length;
        return poly.map(([x, y]) => [cx + (x - cx) * factor, cy + (y - cy) * factor]);
      };

      // Collect eye/nose/mouth/iris polygons across all detected faces
      const eyePolygons = [], nosePolygons = [], lipsPolygons = [];
      const eyeWhitePolygons = [], irisPolygons = [];
      for (const lm of allLandmarks) {
        const toLandmark = (indices) => indices.map((i) => [lm[i].x, lm[i].y]);
        eyePolygons.push(
          expandPoly(toLandmark(LEFT_EYE_IDX),  1.5),
          expandPoly(toLandmark(RIGHT_EYE_IDX), 1.5),
        );
        nosePolygons.push(expandPoly(toLandmark(NOSE_IDX), 1.4));
        lipsPolygons.push(toLandmark(LIPS_IDX));
        eyeWhitePolygons.push(
          expandPoly(toLandmark(LEFT_EYE_IDX),  1.5),
          expandPoly(toLandmark(RIGHT_EYE_IDX), 1.5),
        );
        if (lm.length > 477) {
          irisPolygons.push(toLandmark(IRIS_LEFT_IDX), toLandmark(IRIS_RIGHT_IDX));
        }
      }

      const circlePoly = Array.from({ length: 32 }, (_, i) => {
        const a = (2 * Math.PI * i) / 32 - Math.PI / 2;
        return [0.5 + 0.499 * Math.cos(a), 0.5 + 0.499 * Math.sin(a)];
      });

      const parsed = {
        has_face: hasFace,
        faceCount: allLandmarks.length,
        description: hasFace ? `${allLandmarks.length} rostro(s) detectado(s)` : "Sin rostro detectado",
        segMask, segMaskWidth, segMaskHeight,
        regions: {
          importance_1: { label: "Fondo",             color: "#0f1923", polygons: [] },
          importance_2: { label: "Cabello y cuerpo",  color: "#0d2d4a", polygons: [circlePoly] },
          importance_3: { label: "Rostro",            color: "#7a3510", polygons: [] }, // filled by segMask
          importance_4: { label: "Ojos, nariz, boca", color: "#3d5000", polygons: hasFace
            ? [...eyePolygons, ...nosePolygons, ...lipsPolygons]
            : [] },
          eye_white: { label: "Esclerótica",  color: "#ffffff", polygons: eyeWhitePolygons },
          iris:      { label: "Iris / Pupila", color: "#000000", polygons: irisPolygons },
        },
      };

      addLog(hasFace ? "ok" : "warn", hasFace ? `${allLandmarks.length} rostro(s) detectado(s) ✓` : "Sin rostro claro");
      const res = { parsed, b64 };
      setResult(res);
      return res;

    } catch (err) {
      addLog("err", "Error: " + err.message);
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Un solo botón para el usuario: analiza la foto y lanza las variantes.
  // También lo usa "Volver a generar" tras cambiar parámetros, porque la
  // resolución afecta al recorte y conviene rehacer el análisis.
  const handleGenerarTodo = async () => {
    const res = await analizarImagen();
    if (res) await generarVariantes(res);
  };

  // ── Generate String Art — 3 greedy + 1 Radón simultaneously ──
  const generarVariantes = async (res = result) => {
    if (!res || generating) return;
    setGenerating(true);
    setVariantProgress(VARIANTS.map(() => 0));
    setStringVariants(VARIANTS.map(() => null));
    setShowStringArt(true);
    addLog("info", `Lanzando ${VARIANTS.length} variantes en paralelo (${nailCount} clavos, ${maxLines} líneas máx)...`);

    try {
      const gray = await getGrayscaleData(res.b64, saSize);

      if (posterization > 0) {
        const levels = Math.max(2, Math.round(2 + (1 - posterization / 100) * 14));
        for (let i = 0; i < gray.length; i++) {
          gray[i] = Math.round(gray[i] * levels) / levels;
        }
        addLog("info", `Posterización aplicada (${levels} niveles)`);
      }
      if (overwhite > 0) {
        const thresh = 0.3;
        const k = overwhite / 100;
        for (let i = 0; i < gray.length; i++) {
          if (gray[i] < thresh) gray[i] = gray[i] * (1 - k);
        }
        addLog("info", `Blanqueo aplicado (${overwhite}%)`);
      }

      const importanceMap = buildImportanceMap(res.parsed, saSize, ignoreFeatures, weights);

      workersRef.current.forEach(w => w?.terminate());
      workersRef.current = [];

      let doneCount = 0;

      VARIANTS.forEach((variantDef, idx) => {
        const grayClone = new Float32Array(gray);
        const impClone  = new Float32Array(importanceMap);
        const worker = variantDef.type === "l2" ? createL2Worker() : createStringWorker();
        workersRef.current[idx] = worker;

        const payload = {
          gray: grayClone.buffer,
          importance: impClone.buffer,
          width: saSize,
          nailCount,
          maxLines,
          minDistance: MIN_NAIL_DIST,
          stringWeight: variantDef.weight / 100,
          mode: variantDef.mode || null,
        };

        worker.postMessage(payload, [grayClone.buffer, impClone.buffer]);

        worker.onmessage = (e) => {
          const data = e.data;
          if (data.type === "progress") {
            setVariantProgress(prev => {
              const next = [...prev];
              next[idx] = Math.round((data.line / data.total) * 100);
              return next;
            });
          } else if (data.type === "done") {
            const v = { type: "greedy", sequence: data.sequence, weight: variantDef.weight, label: variantDef.label };

            setStringVariants(prev => { const n = [...prev]; n[idx] = v; return n; });
            setVariantProgress(prev => { const n = [...prev]; n[idx] = 100; return n; });
            addLog("ok", `${variantDef.label} lista: ${data.totalLines} líneas ✓`);

            doneCount++;
            if (doneCount === VARIANTS.length) {
              setGenerating(false);
              // No se cambia de variante: el usuario puede estar mirando una y
              // el salto automático resultaba desconcertante.
              addLog("ok", "Todas las variantes generadas ✓");
            }
          }
        };

        worker.onerror = (err) => {
          addLog("err", `Worker ${variantDef.label} error: ${err.message}`);
          doneCount++;
          if (doneCount === VARIANTS.length) setGenerating(false);
        };
      });
    } catch (err) {
      addLog("err", "Error: " + err.message);
      setGenerating(false);
    }
  };

  // ── Modo nube: 3 variantes (foto normal, fondo blanco y fondo gris con
  // Gemini), todas con el brillo/contraste elegidos y la calidad pedida. ──
  const generarNube = async () => {
    if (!img || generandoNube) return;
    const corrida = ++corridaNubeRef.current;
    const etapasCalidad = CALIDADES_NUBE.find((c) => c.id === calidadNube).etapas;
    const base = recorteAjustado(img, transform, ajusteFoto, CANVAS_SIZE);
    const frio = nubeCaliente() ? 0 : ARRANQUE_FRIO;
    const t0 = Date.now();
    setNube(VARIANTES_NUBE.map((def) => {
      const etapas = [...(def.fondo ? ["gemini"] : []), ...etapasCalidad];
      const est = { ...ESTIMADO, [etapasCalidad[0]]: ESTIMADO[etapasCalidad[0]] + frio };
      return { etapas, etapa: etapas[0], inicio: t0, est, entrada: def.fondo ? null : base, normal: null, maxima: null, error: null };
    }));
    setSelectedVariant(0);
    setVistaCalidad("mejor");
    setShowStringArt(true);
    addLog("info", `Nube: ${VARIANTES_NUBE.length} variantes · calidad ${calidadNube} · ${nailCount} clavos`);

    // Devuelve false si esta corrida ya no es la vigente (nueva foto, salir…).
    const actualizar = (idx, cambio) => {
      if (corridaNubeRef.current !== corrida) return false;
      setNube((prev) => prev.map((v, i) => (i === idx ? { ...v, ...(typeof cambio === "function" ? cambio(v) : cambio) } : v)));
      return true;
    };
    const nombre = `hilorama_${t0}`;

    await Promise.all(VARIANTES_NUBE.map(async (def, idx) => {
      try {
        let entrada = base;
        if (def.fondo) {
          entrada = await quitarFondoGemini(base, def.fondo);
          if (!actualizar(idx, { entrada })) return;
          addLog("ok", `${def.label}: fondo listo (Gemini) ✓`);
        }
        for (let i = 0; i < etapasCalidad.length; i++) {
          const calidad = etapasCalidad[i];
          if (!actualizar(idx, { etapa: calidad, inicio: Date.now() })) return;
          const r = await generarEnNube(entrada, {
            calidad, clavos: nailCount, diametro: frameDiameter, grosor: threadThickness, nombre,
          });
          // La máxima tarda más o menos el doble que la normal en la misma foto.
          const ajusteEst = etapasCalidad[i + 1] === "maxima"
            ? (v) => ({ est: { ...v.est, maxima: Math.max(ESTIMADO.maxima * 0.6, r.segundos * 2 + 4) } })
            : () => ({});
          if (!actualizar(idx, (v) => ({ [calidad]: r, ...ajusteEst(v) }))) return;
          addLog("ok", `${def.label} · ${calidad}: ${r.lineas} hilos en ${r.segundos}s ✓`);
        }
        actualizar(idx, { etapa: "listo" });
      } catch (err) {
        const msg = mensajeError(err);
        console.error(`Nube ${def.label}:`, err);
        actualizar(idx, { etapa: "error", error: msg });
        addLog("err", `${def.label}: ${msg}`);
      }
    }));
  };

  const handleDownloadImage = () => {
    const v = stringVariants[selectedVariant];
    if (!v?.sequence) return;
    const size = 2400;
    const canvas = document.createElement("canvas");
    canvas.width = size; canvas.height = size;
    const ctx = canvas.getContext("2d");
    const margin = Math.round(52 * size / VARIANT_CANVAS_SIZE);
    const r = size / 2 - margin;
    const cx = size / 2, cy = size / 2;

    // White background
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, size, size);

    // Clip to circle
    ctx.save();
    ctx.beginPath();
    ctx.arc(cx, cy, r + margin - 2, 0, Math.PI * 2);
    ctx.clip();
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, size, size);

    // Nail positions
    const nailX = [], nailY = [];
    for (let i = 0; i < nailCount; i++) {
      const angle = (2 * Math.PI * i) / nailCount;
      nailX.push(cx + r * Math.cos(angle));
      nailY.push(cy + r * Math.sin(angle));
    }

    // Lines — draw each individually at opacity 0.55 to match CircleSequence SVG rendering
    const scale = size / VARIANT_CANVAS_SIZE;
    const lw = (lineThickness / 100) * scale;
    ctx.strokeStyle = "#111111";
    ctx.lineWidth = lw;
    ctx.globalAlpha = 0.55;
    const seq = v.sequence;
    for (let i = 0; i < seq.length - 1; i++) {
      ctx.beginPath();
      ctx.moveTo(nailX[seq[i]], nailY[seq[i]]);
      ctx.lineTo(nailX[seq[i+1]], nailY[seq[i+1]]);
      ctx.stroke();
    }
    ctx.globalAlpha = 1;
    ctx.restore();

    // Circle border
    ctx.strokeStyle = "#cccccc";
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(cx, cy, r + margin - 2, 0, Math.PI * 2);
    ctx.stroke();

    canvas.toBlob((blob) => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `hilorama_${nailCount}clavos_${VARIANTS[selectedVariant].label}_${seq.length - 1}lineas.png`;
      a.click();
      URL.revokeObjectURL(url);
    }, "image/png");
    addLog("ok", "Imagen descargada ✓");
  };

  const handleDownloadSequence = () => {
    const v = stringVariants[selectedVariant];
    if (!v) return;
    const text = v.sequence.join("\n");
    const filename = `stringart_${nailCount}nails_${v.weight}pct_${v.sequence.length - 1}lines.txt`;
    const blob = new Blob([text], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleDownloadJSON = () => {
    const v = stringVariants[selectedVariant];
    if (!v) return;
    const numStrings = v.sequence.length - 1;
    const stringLengthM = (numStrings * frameDiameter * Math.PI / nailCount / 1000);
    const json = {
      Apps: "Hilorama_Studio",
      NumPins: nailCount,
      TH_Color: "#101010",
      BG_Color: "#FFFFFF",
      Strings: numStrings,
      StringWidth: threadThickness,
      StringLength: parseFloat(stringLengthM.toFixed(1)),
      WorkDiam: frameDiameter,
      Variant: VARIANTS[selectedVariant].label,
      Chain: Array.from(v.sequence),
    };
    const blob = new Blob([JSON.stringify(json, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `hilorama_${nailCount}clavos_${numStrings}lineas.json`;
    a.click();
    URL.revokeObjectURL(url);
    addLog("ok", `JSON exportado (${stringLengthM.toFixed(1)}m de hilo) ✓`);
  };

  const touchRef = useRef({});

  const onTouchStart = useCallback((e) => {
    if (e.touches.length === 1) {
      touchRef.current = { type: "pan", x: e.touches[0].clientX, y: e.touches[0].clientY };
    } else if (e.touches.length === 2) {
      const t1 = e.touches[0], t2 = e.touches[1];
      touchRef.current = {
        type: "pinch",
        dist: Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY),
        angle: Math.atan2(t2.clientY - t1.clientY, t2.clientX - t1.clientX) * 180 / Math.PI,
        zoom: transform.zoom,
        rot: transform.rot,
      };
    }
  }, [transform]);

  const onTouchMove = useCallback((e) => {
    e.preventDefault();
    if (e.touches.length === 1 && touchRef.current.type === "pan") {
      const k = scaleRef.current || 1;
      const dx = (e.touches[0].clientX - touchRef.current.x) / k;
      const dy = (e.touches[0].clientY - touchRef.current.y) / k;
      touchRef.current = { type: "pan", x: e.touches[0].clientX, y: e.touches[0].clientY };
      setTransform(t => desplazarEnPantalla(t, dx, dy));
    } else if (e.touches.length === 2 && touchRef.current.type === "pinch") {
      const t1 = e.touches[0], t2 = e.touches[1];
      const newDist = Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY);
      const newAngle = Math.atan2(t2.clientY - t1.clientY, t2.clientX - t1.clientX) * 180 / Math.PI;
      // Hay que guardar la distancia y el ángulo previos ANTES de actualizar
      // touchRef: si se lee después, el factor de zoom sale siempre 1.
      const prevDist = touchRef.current.dist;
      const dRot = newAngle - touchRef.current.angle;
      touchRef.current = { ...touchRef.current, dist: newDist, angle: newAngle };
      const factor = prevDist > 0 ? newDist / prevDist : 1;
      setTransform(t => ({
        ...t,
        zoom: Math.max(0.1, Math.min(6, t.zoom * factor)),
        rot: (t.rot + dRot + 360) % 360,
      }));
    }
  }, []);

  const onTouchEnd = useCallback((e) => {
    if (e?.touches?.length === 1) {
      touchRef.current = { type: "pan", x: e.touches[0].clientX, y: e.touches[0].clientY };
    } else {
      touchRef.current = {};
    }
  }, []);

  // ── RENDER ────────────────────────────────────────────────────────────────
  // Lo que se muestra sale de los workers (clásico) o de la nube.
  const VARIANTES = esNube ? VARIANTES_NUBE : VARIANTS;
  const generandoVista = esNube ? generandoNube : generating;
  const restanteNube = (v) => {
    if (!v || v.etapa === "listo" || v.etapa === "error") return 0;
    const pasado = (ahora - v.inicio) / 1000;
    let r = Math.max(3, v.est[v.etapa] - pasado);
    for (const e of v.etapas.slice(v.etapas.indexOf(v.etapa) + 1)) r += v.est[e];
    return r;
  };
  const resultadoNube = (v) => (!v ? null : (vistaCalidad === "normal" && v.normal) ? v.normal : (v.maxima || v.normal));
  const variantesVista = esNube
    ? nube.map((v) => {
        const r = resultadoNube(v);
        return r ? { sequence: r.sequence, calidad: r === v.maxima ? "maxima" : "normal" } : null;
      })
    : stringVariants;
  const progresoVista = esNube
    ? nube.map((v) => {
        if (!v) return 0;
        if (v.etapa === "listo" || v.etapa === "error") return 100;
        const total = v.etapas.reduce((a, e) => a + v.est[e], 0);
        return Math.round(Math.min(97, Math.max(0, (1 - restanteNube(v) / total) * 100)));
      })
    : variantProgress;
  const restanteTotal = esNube ? Math.max(0, ...nube.map(restanteNube)) : 0;
  const reloj = (seg) => {
    const t = Math.ceil(seg);
    return `${Math.floor(t / 60)}:${String(t % 60).padStart(2, "0")}`;
  };
  const textoEtapa = { gemini: "Quitando fondo (Gemini)", normal: "Calculando · normal", maxima: "Calculando · máxima" };
  const seleccionada = variantesVista[selectedVariant];

  const nombrePorDefecto = () => {
    const fecha = new Date().toLocaleDateString("es-MX", { day: "2-digit", month: "short" });
    return "Hilorama " + VARIANTES[selectedVariant].label + " · " + fecha;
  };

  const guardarConNombre = async (nombre) => {
    const limpio = (nombre || "").trim();
    if (!seleccionada || !limpio || guardando) return;
    setErrorUso("");
    setGuardando(true);
    try {
      await onUsarSecuencia({
        secuencia: Array.from(seleccionada.sequence),
        clavos: nailCount,
        variante: VARIANTES[selectedVariant].label + (esNube && seleccionada.calidad ? ` · ${seleccionada.calidad}` : ""),
        nombre: limpio,
      });
      setPidiendoNombre(false);
    } catch (err) {
      setErrorUso(err?.message || "No se pudo guardar la secuencia");
    } finally {
      setGuardando(false);
    }
  };
  // Fase derivada del estado: sin foto → foto; con foto → encuadre; con
  // variantes (o generándolas) → resultado.
  const phase = !img ? 0 : showStringArt ? 2 : 1;
  const ocupado = loading || generandoVista;
  const progresoTotal = Math.round(progresoVista.reduce((a, b) => a + b, 0) / VARIANTES.length);
  const clavosNoEstandar = nailCount !== CLAVOS_ESTANDAR;

  return (
    <div ref={rootRef} className="sa-studio" style={S.root}>
      <style>{GLOBAL_CSS}</style>

      {/* HEADER */}
      <div ref={headerRef} style={{ ...S.header, padding: movil ? "10px 12px" : "14px 20px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
          {onCancel && (
            <button className="btn-sec" style={{ ...S.ctrlBtn, padding: "8px 12px", minHeight: 36 }} onClick={onCancel}>
              <ArrowLeft size={16} /> {movil ? "" : "Volver"}
            </button>
          )}
          <h2 style={S.titulo}>
            <Sparkles size={20} style={{ color: AZUL_CLARO }} />
            {movil ? "Hilorama" : "Generador de Hiloramas"}
            {esNube && <span style={S.etiquetaNube}><Cloud size={12} /> Nube</span>}
          </h2>
        </div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", justifyContent: "flex-end" }}>
          {PHASE_LABELS.map((label, i) => {
            const [num, texto] = label.split(" · ");
            const activo = i === phase;
            return (
              <div key={i} style={{
                ...S.pill,
                ...(activo ? S.pillActive : {}),
                ...(i > phase ? { opacity: 0.4 } : {}),
              }}>
                {/* En móvil solo cabe una fila: el paso hecho muestra ✓ en lugar del número */}
                {movil && i < phase ? <Check size={12} /> : <span style={S.pillNum}>{num}</span>}
                {!movil && texto}
                {!movil && i < phase && <Check size={11} />}
              </div>
            );
          })}
        </div>
      </div>

      {/* MAIN SCROLLABLE AREA */}
      <div ref={mainRef} style={{ ...S.main, padding: movil ? "16px 12px 24px" : "24px 20px 32px" }}>

        {/* ── PASO 1: FOTO ── */}
        {!img && (
          <div style={{ ...S.phase, marginTop: "auto", marginBottom: "auto" }}>
           <div style={{
             display: "flex", flexDirection: compacto ? "column" : "row",
             alignItems: "center", justifyContent: "center", gap: compacto ? 18 : 36,
           }}>
            <label style={{ ...S.bigUpload, width: uploadSize, height: uploadSize, margin: 0 }} className="upload-zone"
              onDrop={(e) => { e.preventDefault(); handleFile(e.dataTransfer.files[0]); }}
              onDragOver={(e) => e.preventDefault()}>
              <input type="file" accept="image/*" style={{ display: "none" }}
                onChange={(e) => handleFile(e.target.files[0])} />
              <div style={{
                width: 88, height: 88, borderRadius: "50%",
                background: "rgba(30,41,59,0.8)", border: "2px dashed rgba(71,85,105,0.8)",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                <ImagePlus size={34} style={{ color: TENUE }} />
              </div>
              <span style={{ fontSize: 13, color: TENUE, textAlign: "center", lineHeight: 1.7, padding: "0 24px" }}>
                <span style={{ display: "block", color: "#fff", fontWeight: 700, marginBottom: 4 }}>
                  Elige una foto
                </span>
                Toca aquí o arrastra la imagen
              </span>
            </label>
            <div style={{ ...S.qrCard, width: compacto ? Math.min(avail, 300) : 220 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, color: "#fff", fontWeight: 700, fontSize: 13 }}>
                <Smartphone size={16} style={{ color: AZUL_CLARO }} /> Desde tu teléfono
              </div>
              <div style={{
                width: qrSize, height: qrSize, borderRadius: 12, background: "#fff", padding: 6,
                display: "flex", alignItems: "center", justifyContent: "center", position: "relative",
              }}>
                {qrDataUrl
                  ? <img src={qrDataUrl} alt="QR para enviar una foto desde el teléfono" style={{ width: "100%", height: "100%", display: "block" }} />
                  : <span style={{ ...S.spinner, borderColor: "rgba(15,23,42,0.25)", borderTopColor: "#0f172a", width: 22, height: 22 }} />}
                {recibiendoQR && (
                  <div style={{
                    position: "absolute", inset: 0, borderRadius: 12, background: "rgba(15,23,42,0.85)",
                    display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 8,
                    color: "#fff", fontSize: 12, fontWeight: 700,
                  }}>
                    <span style={S.spinner} /> Recibiendo…
                  </div>
                )}
              </div>
              <span style={{ ...S.hint, fontSize: 11 }}>
                Escanea el código para tomar o subir una foto desde el celular. Aparecerá aquí al instante.
              </span>
            </div>
           </div>
            {!bajo && <div style={{ ...S.hint, maxWidth: 360 }}>
              Funciona mejor con un retrato de frente, bien iluminado y con el rostro grande.
              {!modelReady && <span style={{ display: "block", marginTop: 6, color: AZUL_CLARO }}>Preparando el detector de rostros…</span>}
            </div>}
          </div>
        )}

        {/* ── PASO 2: ENCUADRE ── */}
        {img && !showStringArt && (() => {
          const titulo = (
            <div style={{ ...S.stepTitle, ...(lateral ? { maxWidth: "none" } : {}) }}>
              <span style={S.stepNum}>2</span>
              <div>
                <div style={{ color: "#fff", fontWeight: 700, fontSize: 15 }}>Ajusta el encuadre</div>
                {!bajo && (
                  <div style={{ fontSize: 12, color: TENUE }}>
                    {movil
                      ? "El rostro se centra solo. Arrastra para mover, pellizca para acercar."
                      : "El rostro se centra solo. Arrastra la foto para moverla, usa la rueda para acercar y gira con el dial."}
                  </div>
                )}
              </div>
            </div>
          );

          const lienzo = (
            <div style={{ position: "relative", width: wrapSize, height: wrapSize, flexShrink: 0, touchAction: "none" }}
              onTouchStart={onTouchStart}
              onTouchMove={onTouchMove}
              onTouchEnd={onTouchEnd}>
              <canvas ref={mainCanvasRef} width={CANVAS_SIZE} height={CANVAS_SIZE}
                style={{ position: "absolute", top: dialPad, left: dialPad, width: canvasDisp, height: canvasDisp, borderRadius: "50%", cursor: "grab", display: "block", touchAction: "none",
                  ...(esNube ? { filter: `brightness(${ajusteFoto.brillo}%) contrast(${ajusteFoto.contraste}%)` } : {}) }}
                onMouseDown={onMouseDown} onWheel={onWheel} />
              <canvas ref={nailCanvasRef} width={CANVAS_SIZE} height={CANVAS_SIZE}
                style={{ position: "absolute", top: dialPad, left: dialPad, width: canvasDisp, height: canvasDisp, borderRadius: "50%", pointerEvents: "none" }} />
              <RotationDial
                rotation={transform.rot}
                wrapSize={wrapSize}
                onRotate={(rot) => setTransform(t => ({ ...t, rot }))}
              />
            </div>
          );

          const anchoBoton = lateral ? { width: "100%", justifyContent: "flex-start" } : {};
          const controles = (
            <>
              <label style={{ ...S.smallUpload, ...anchoBoton }} className="btn-sec">
                <input type="file" accept="image/*" style={{ display: "none" }}
                  onChange={(e) => handleFile(e.target.files[0])} />
                <ImageIcon size={15} /> Cambiar foto
              </label>
              <button className="btn-sec"
                style={{ ...S.ctrlBtn, ...anchoBoton, ...(modelReady ? {} : { opacity: 0.4, cursor: "not-allowed" }) }}
                onClick={handleAutoFrame} disabled={!modelReady}>
                <ScanFace size={15} /> Centrar rostro
              </button>
              <button className="btn-sec" style={{ ...S.ctrlBtn, ...anchoBoton }}
                onClick={() => setTransform({ x: 0, y: 0, zoom: fitZoom(img), rot: 0 })}>
                <RotateCcw size={15} /> Ver foto completa
              </button>
            </>
          );

          // Brillo, contraste y calidad: solo en el modo nube. La vista previa
          // usa el mismo filtro que luego se aplica a la foto que se envía.
          const etapasElegidas = CALIDADES_NUBE.find((c) => c.id === calidadNube).etapas;
          const estimadoNube = ESTIMADO.gemini + etapasElegidas.reduce((a, e) => a + ESTIMADO[e], 0)
            + (nubeCaliente() ? 0 : ARRANQUE_FRIO);
          const ajustesNube = esNube && (
            <div style={{ ...S.card, gap: 10, padding: "12px 14px", width: "100%", maxWidth: lateral ? "none" : 420 }}>
              <Slider label="Brillo" unit="%" min={50} max={150} value={ajusteFoto.brillo}
                onChange={(v) => setAjusteFoto((a) => ({ ...a, brillo: v }))} />
              <Slider label="Contraste" unit="%" min={50} max={200} value={ajusteFoto.contraste}
                onChange={(v) => setAjusteFoto((a) => ({ ...a, contraste: v }))} />
              {(ajusteFoto.brillo !== 100 || ajusteFoto.contraste !== 100) && (
                <button className="btn-sec" style={{ ...S.ctrlBtn, padding: "6px 10px", minHeight: 30, fontSize: 12, alignSelf: "flex-start" }}
                  onClick={() => setAjusteFoto({ brillo: 100, contraste: 100 })}>
                  <RotateCcw size={13} /> Restablecer
                </button>
              )}
              <div style={S.ctrlLabel}>Calidad</div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {CALIDADES_NUBE.map((c) => (
                  <button key={c.id} className="btn-sec"
                    style={{ ...S.ctrlBtn, padding: "6px 10px", minHeight: 30, fontSize: 12,
                      ...(calidadNube === c.id ? { color: "#fff", border: "1px solid rgba(59,130,246,0.7)", background: "rgba(37,99,235,0.25)" } : {}) }}
                    onClick={() => setCalidadNube(c.id)}>
                    {c.label}
                  </button>
                ))}
              </div>
            </div>
          );

          const generar = (
            <button className="btn-primary" style={{ ...S.mainBtn, width: "100%", maxWidth: lateral ? "none" : 420 }}
              onClick={esNube ? generarNube : handleGenerarTodo}
              disabled={!img || ocupado || (!esNube && !modelReady)}>
              {loading
                ? <><span style={S.spinner} /> Analizando el rostro…</>
                : esNube
                  ? <><Cloud size={17} /> Generar en la nube</>
                  : !modelReady
                    ? <><span style={S.spinner} /> Preparando…</>
                    : <><Sparkles size={17} /> Generar hilorama</>}
            </button>
          );

          const ayuda = !bajo && (
            <div style={S.hint}>
              {esNube
                ? <><Clock size={12} style={{ verticalAlign: "-2px" }} /> Tiempo aproximado: {reloj(estimadoNube)}. Se generan 3 versiones: foto normal, fondo blanco y fondo gris.</>
                : "Tarda entre unos segundos y un minuto según el dispositivo."}
            </div>
          );

          if (lateral) {
            return (
              <div style={{ display: "flex", gap: 24, alignItems: "center", justifyContent: "center", width: "100%", flexShrink: 0 }}>
                {lienzo}
                <div style={{ display: "flex", flexDirection: "column", gap: 14, width: anchoPanel, flexShrink: 0 }}>
                  {titulo}
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>{controles}</div>
                  {ajustesNube}
                  {generar}
                  {ayuda}
                </div>
              </div>
            );
          }

          return (
            <div style={S.phase}>
              {titulo}
              {lienzo}
              <div style={S.controls}>{controles}</div>
              {ajustesNube}
              {generar}
              {ayuda}
            </div>
          );
        })()}

        {/* ── PASO 3: RESULTADO ── */}
        {img && showStringArt && (
          <div style={S.phase}>
            {(() => {
              const titulo = (
                <div style={{ ...S.stepTitle, ...(lateral ? { maxWidth: "none" } : {}) }}>
                  <span style={S.stepNum}>3</span>
                  <div>
                    <div style={{ color: "#fff", fontWeight: 700, fontSize: 15 }}>
                      {!generandoVista
                        ? "Elige el resultado"
                        : esNube
                          ? <><Clock size={14} className="sa-espera" style={{ verticalAlign: "-2px" }} /> Faltan ~{reloj(restanteTotal)}</>
                          : `Generando… ${progresoTotal}%`}
                    </div>
                    {!bajo && (
                      <div style={{ fontSize: 12, color: TENUE }}>
                        {generandoVista
                          ? esNube
                            ? "La nube calcula las tres versiones; cada una aparece en cuanto llega."
                            : "Se preparan seis versiones a la vez; puedes ir mirándolas mientras terminan."
                          : "Compara las versiones y confirma la que quieras hilar."}
                      </div>
                    )}
                  </div>
                </div>
              );

              const carrusel = (
                <div style={{ display: "flex", alignItems: "center", gap: compacto ? 6 : 12, maxWidth: "100%" }}>
                  {/* Prev thumb */}
                  {!compacto && (
                  <div style={{ width: 72, display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
                    {selectedVariant > 0 && (
                      <div style={{ cursor: "pointer", opacity: 0.5 }} onClick={() => setSelectedVariant(v => v - 1)}>
                        <div style={{ width: 72, height: 72, borderRadius: "50%", overflow: "hidden", border: "1px solid rgba(51,65,85,0.6)" }}>
                          <CircleSequence sequence={variantesVista[selectedVariant - 1]?.sequence ?? []} nailCount={nailCount} lineWidth={lineThickness / 100} size={VARIANT_CANVAS_SIZE} />
                        </div>
                        <span style={{ display: "block", fontFamily: MONO, fontSize: 9, color: ETIQUETA, textAlign: "center", marginTop: 4 }}>{VARIANTES[selectedVariant - 1].label}</span>
                      </div>
                    )}
                  </div>
                  )}

                  <button className="btn-sec"
                    onClick={() => setSelectedVariant(v => Math.max(0, v - 1))}
                    disabled={selectedVariant === 0} style={{ ...S.arrowBtn, opacity: selectedVariant === 0 ? 0.25 : 1 }}>
                    <ChevronLeft size={20} />
                  </button>

                  <div style={{ position: "relative", flexShrink: 0 }}>
                    {VARIANTES.map((_, idx) => (
                      <div key={idx} style={{ display: selectedVariant === idx ? "block" : "none" }}>
                        <div style={{ borderRadius: "50%", overflow: "hidden", border: `2px solid ${variantesVista[idx] ? "rgba(59,130,246,0.6)" : "rgba(51,65,85,0.6)"}`, boxShadow: variantesVista[idx] ? "0 0 30px rgba(37,99,235,0.25)" : "none", width: variantDisp, height: variantDisp }}>
                          <CircleSequence sequence={variantesVista[idx]?.sequence ?? []} nailCount={nailCount} lineWidth={lineThickness / 100} size={VARIANT_CANVAS_SIZE} />
                        </div>
                        {!variantesVista[idx] && (
                          <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 10, borderRadius: "50%", background: "rgba(15,23,42,0.75)", backdropFilter: "blur(2px)" }}>
                            {esNube && nube[idx]?.etapa === "error" ? (
                              <span style={{ fontSize: 12, color: "#fca5a5", textAlign: "center", maxWidth: "70%" }}>{nube[idx].error}</span>
                            ) : (
                              <>
                                {esNube && nube[idx] && (
                                  <span style={{ fontSize: 12, color: TENUE, display: "flex", alignItems: "center", gap: 6 }}>
                                    <Clock size={13} className="sa-espera" /> {textoEtapa[nube[idx].etapa]}
                                  </span>
                                )}
                                <span style={{ fontFamily: MONO, fontSize: 12, fontWeight: 700, color: AZUL_CLARO }}>
                                  {esNube && nube[idx] ? `~${reloj(restanteNube(nube[idx]))}` : `${progresoVista[idx]}%`}
                                </span>
                                <div style={{ width: 120, background: "#334155", borderRadius: 999, height: 4, overflow: "hidden" }}>
                                  <div style={{ width: `${progresoVista[idx]}%`, height: "100%", background: "linear-gradient(to right, #2563eb, #60a5fa)", transition: "width 0.4s" }} />
                                </div>
                              </>
                            )}
                          </div>
                        )}
                        {/* Ya hay versión normal y la máxima sigue en camino */}
                        {esNube && variantesVista[idx] && nube[idx]?.etapa === "maxima" && (
                          <div style={S.avisoMaxima}>
                            <Clock size={12} className="sa-espera" /> Máxima en ~{reloj(restanteNube(nube[idx]))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>

                  <button className="btn-sec" style={{ ...S.arrowBtn, opacity: selectedVariant === VARIANTES.length - 1 ? 0.25 : 1 }}
                    onClick={() => setSelectedVariant(v => Math.min(VARIANTES.length - 1, v + 1))}
                    disabled={selectedVariant === VARIANTES.length - 1}>
                    <ChevronRight size={20} />
                  </button>

                  {/* Next thumb */}
                  {!compacto && (
                  <div style={{ width: 72, display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
                    {selectedVariant < VARIANTES.length - 1 && (
                      <div style={{ cursor: "pointer", opacity: 0.5 }} onClick={() => setSelectedVariant(v => v + 1)}>
                        <div style={{ width: 72, height: 72, borderRadius: "50%", overflow: "hidden", border: "1px solid rgba(51,65,85,0.6)" }}>
                          <CircleSequence sequence={variantesVista[selectedVariant + 1]?.sequence ?? []} nailCount={nailCount} lineWidth={lineThickness / 100} size={VARIANT_CANVAS_SIZE} />
                        </div>
                        <span style={{ display: "block", fontFamily: MONO, fontSize: 9, color: ETIQUETA, textAlign: "center", marginTop: 4 }}>{VARIANTES[selectedVariant + 1].label}</span>
                      </div>
                    )}
                  </div>
                  )}
                </div>
              );

              const acciones = (
                <>
                  {onUsarSecuencia && (
                    <button
                      className="btn-primary"
                      style={{ ...S.mainBtn, width: "100%", maxWidth: lateral ? "none" : 420 }}
                      disabled={!seleccionada || guardando}
                      onClick={() => {
                        if (!seleccionada) return;
                        setErrorUso("");
                        setNombreSecuencia(nombrePorDefecto());
                        setPidiendoNombre(true);
                      }}
                    >
                      {guardando
                        ? <><span style={S.spinner} /> Guardando…</>
                        : <><Check size={18} /> Usar esta secuencia</>}
                    </button>
                  )}

                  {errorUso && (
                    <div style={{
                      fontSize: 12, color: "#fca5a5", textAlign: "center", maxWidth: 420,
                      background: "rgba(239,68,68,0.1)", border: "1px solid rgba(248,113,113,0.3)",
                      borderRadius: 10, padding: "8px 12px",
                    }}>{errorUso}</div>
                  )}

                  {/* Nube: elegir calidad cuando ya están las dos, y ver la foto que se envió */}
                  {esNube && nube[selectedVariant]?.normal && nube[selectedVariant]?.maxima && (
                    <div style={{ display: "flex", gap: 6, justifyContent: "center" }}>
                      {[["normal", "Normal"], ["mejor", "Máxima"]].map(([id, texto]) => (
                        <button key={id} className="btn-sec"
                          style={{ ...S.ctrlBtn, padding: "6px 12px", minHeight: 30, fontSize: 12,
                            ...(vistaCalidad === id ? { color: "#fff", border: "1px solid rgba(59,130,246,0.7)", background: "rgba(37,99,235,0.25)" } : {}) }}
                          onClick={() => setVistaCalidad(id)}>
                          {texto}
                        </button>
                      ))}
                    </div>
                  )}
                  {esNube && nube[selectedVariant]?.entrada && (
                    <div style={{ display: "flex", alignItems: "center", gap: 10, justifyContent: "center" }}>
                      <img src={nube[selectedVariant].entrada} alt="Imagen enviada a la nube"
                        style={{ width: 64, height: 64, borderRadius: "50%", objectFit: "cover", border: `1px solid ${BORDE}` }} />
                      <span style={{ fontSize: 11, color: ETIQUETA }}>Imagen enviada</span>
                    </div>
                  )}

                  {seleccionada && (
                    <div style={{ fontSize: 11, color: ETIQUETA, textAlign: "center" }}>
                      Versión {VARIANTES[selectedVariant].label}{esNube && seleccionada.calidad ? ` · ${seleccionada.calidad === "maxima" ? "máxima" : "normal"}` : ""} · {seleccionada.sequence.length - 1} líneas · {nailCount} clavos
                      {clavosNoEstandar && <span style={{ color: "#fbbf24" }}> · fuera del estándar</span>}
                    </div>
                  )}

                  <div style={{ display: "flex", gap: 10, flexWrap: "wrap", justifyContent: "center" }}>
                    <button className="btn-sec" style={{ ...S.ctrlBtn, ...(lateral ? { flex: 1 } : {}) }} disabled={generandoVista}
                      onClick={() => setShowStringArt(false)}>
                      <ArrowLeft size={15} /> Encuadre
                    </button>
                    {!esNube && (
                    <button className="btn-sec"
                      style={{ ...S.ctrlBtn, ...(lateral ? { flex: 1 } : {}), ...(ajustesAbiertos ? { color: AZUL_CLARO, border: "1px solid rgba(59,130,246,0.4)" } : {}) }}
                      onClick={() => setAjustesAbiertos(a => !a)}>
                      <SlidersHorizontal size={15} /> Ajustes
                      {ajustesAbiertos ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                    </button>
                    )}
                  </div>
                </>
              );

              // Fila de relojes: siempre encima de la previsualización.
              const relojes = (
                <div style={S.estadoLista}>
                  {VARIANTES.map((v, idx) => {
                    // En la nube "lista" es que terminaron todas sus calidades.
                    const vn = esNube ? nube[idx] : null;
                    const fallo = vn?.etapa === "error";
                    const lista = esNube ? vn?.etapa === "listo" : !!variantesVista[idx];
                    const espera = esNube ? (vn && !lista && !fallo ? `~${reloj(restanteNube(vn))}` : "") : `${progresoVista[idx]}%`;
                    const sel = selectedVariant === idx;
                    return (
                      <button
                        key={idx}
                        className="btn-sec"
                        onClick={() => setSelectedVariant(idx)}
                        style={{
                          ...S.estadoChip,
                          ...(sel ? S.estadoChipSel : {}),
                          ...(lista ? {} : { opacity: 0.75 }),
                        }}
                        title={fallo ? `${v.label} · ${vn.error}` : lista ? `${v.label} · lista` : `${v.label} · calculando ${espera}`}
                      >
                        {fallo
                          ? <AlertTriangle size={12} style={{ color: "#f87171" }} />
                          : lista
                            ? <Check size={12} style={{ color: sel ? "#fff" : "#34d399" }} />
                            : <Clock size={12} className="sa-espera" />}
                        <span>{v.label}</span>
                        {!lista && espera && <span style={{ fontFamily: MONO, opacity: 0.7 }}>{espera}</span>}
                      </button>
                    );
                  })}
                </div>
              );

              if (lateral) {
                return (
                  <>
                    {relojes}
                    <div style={{ display: "flex", gap: 24, alignItems: "center", justifyContent: "center", width: "100%", flexShrink: 0 }}>
                      {carrusel}
                      <div style={{ display: "flex", flexDirection: "column", gap: 14, width: anchoPanel, flexShrink: 0 }}>
                        {titulo}
                        {acciones}
                      </div>
                    </div>
                  </>
                );
              }

              return (
                <>
                  {titulo}
                  {relojes}
                  {carrusel}
                  {acciones}
                </>
              );
            })()}

            {/* ── AJUSTES AVANZADOS (opcionales) ── */}
            {ajustesAbiertos && result && (
              <div style={{ ...S.phase, gap: 14 }}>
                <div style={{ ...S.hint, maxWidth: 520 }}>
                  Nada de esto es necesario para un buen resultado. Cambia lo que quieras y pulsa
                  <b style={{ color: "#fff" }}> Volver a generar</b>.
                </div>

                {/* Mapa de importancias */}
                <div style={S.card}>
                  <div style={S.cardLabel}>Zonas detectadas</div>
                  <div style={{ display: "flex", gap: 16, alignItems: "flex-start", flexWrap: "wrap", justifyContent: "center" }}>
                    <CanvasCard ref={origRef} label="Original" size={cardSize} />
                    <CanvasCard ref={maskRef} label="Importancias" size={cardSize} />
                    <CanvasCard ref={overlayRef} label="Superposición" size={cardSize} />
                  </div>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "center" }}>
                    {IMPORTANCE_LEGEND.map((item) => (
                      <div key={item.key} style={S.chip}>
                        <div style={{ width: 9, height: 9, borderRadius: 3, background: item.color, border: `1px solid ${item.border}`, flexShrink: 0 }} />
                        <span style={{ color: TENUE, fontSize: 11 }}>{item.label}</span>
                        <span style={{ color: AZUL_CLARO, fontFamily: MONO, fontSize: 10, fontWeight: 700 }}>{item.imp}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Pesos de importancia */}
                <div style={S.card}>
                  <div style={S.cardLabel}>Qué zonas resaltar</div>
                  <div style={S.hint}>Más peso = más hilo en esa zona. Los rasgos del rostro suelen ir por encima de 1.</div>
                  {[
                    { key: "bg",         label: "Fondo"           },
                    { key: "hair",       label: "Cabello"         },
                    { key: "body",       label: "Piel cuerpo"     },
                    { key: "face",       label: "Piel rostro"     },
                    { key: "nose_mouth", label: "Nariz · Boca",    disabled: ignoreFeatures },
                    { key: "eye_oval",   label: "Borde párpados",  disabled: ignoreFeatures },
                    { key: "eye_white",  label: "Esclerótica ↓",   disabled: ignoreFeatures },
                    { key: "iris",       label: "Iris / Pupila",   disabled: ignoreFeatures },
                  ].map(({ key, label, disabled }) => (
                    <div key={key} style={{ display: "flex", alignItems: "center", gap: 10, opacity: disabled ? 0.35 : 1 }}>
                      <span style={{ fontSize: 11, color: TENUE, width: 110, flexShrink: 0 }}>{label}</span>
                      <input type="range" min={0} max={50} step={1} value={Math.round(weights[key] * 10)}
                        disabled={disabled}
                        onChange={(e) => setW(key)(Number(e.target.value) / 10)}
                        style={{ flex: 1 }} />
                      <span style={{ fontFamily: MONO, fontSize: 11, fontWeight: 700, color: AZUL_CLARO, width: 34, textAlign: "right" }}>×{weights[key].toFixed(1)}</span>
                    </div>
                  ))}
                  <label style={{ display: "flex", alignItems: "center", gap: 9, cursor: "pointer", userSelect: "none" }}>
                    <input type="checkbox" checked={ignoreFeatures} onChange={(e) => setIgnoreFeatures(e.target.checked)}
                      style={{ accentColor: AZUL, width: 15, height: 15 }} />
                    <span style={{ fontSize: 12, color: ignoreFeatures ? AZUL_CLARO : TENUE }}>
                      Ignorar ojos · nariz · boca
                    </span>
                  </label>
                </div>

                {/* Trazado */}
                <div style={S.card}>
                  <div style={S.cardLabel}>Trazado</div>
                  <Slider label="Máx líneas" unit="" min={500} max={8000} value={maxLines} onChange={setMaxLines} />
                  <Slider label="Resolución" unit="px" min={200} max={1024} value={saSize} onChange={setSaSize} />
                  <Slider label="Posterización" unit="%" min={0} max={100} value={posterization} onChange={setPosterization} />
                  <Slider label="Blanqueo" unit="%" min={0} max={100} value={overwhite} onChange={setOverwhite} />
                </div>

                {/* Clavos: configuración excepcional */}
                <div style={{ ...S.card, ...(clavosNoEstandar ? { border: "1px solid rgba(251,191,36,0.4)" } : {}) }}>
                  <div style={S.cardLabel}>Clavos de la máquina</div>
                  <ClavosControl nailCount={nailCount} setNailCount={setNailCount} />
                  <div style={S.hint}>
                    La máquina está preparada para <b style={{ color: "#fff" }}>{CLAVOS_ESTANDAR}</b> clavos (mínimo {CLAVOS_MIN}).
                    {clavosNoEstandar
                      ? <span style={{ color: "#fbbf24" }}> Esta secuencia quedará marcada como generada con un número de clavos distinto al habitual.</span>
                      : " Cámbialo solo si el bastidor es distinto."}
                  </div>
                </div>

                <button className="btn-primary" style={{ ...S.mainBtn, width: "100%", maxWidth: 420 }}
                  onClick={handleGenerarTodo} disabled={ocupado}>
                  {ocupado
                    ? <><span style={S.spinner} /> {loading ? "Analizando…" : `Generando ${progresoTotal}%`}</>
                    : <><RefreshCw size={17} /> Volver a generar</>}
                </button>

                {/* Bastidor y exportación */}
                <div style={S.card}>
                  <div style={S.cardLabel}>Bastidor y exportación</div>
                  <Slider label="Grosor del trazo en pantalla" unit={` px (${(lineThickness/100).toFixed(2)})`} min={10} max={40} value={lineThickness} onChange={setLineThickness} />
                  <div style={{ display: "flex", gap: 16, alignItems: "center", flexWrap: "wrap" }}>
                    <div style={S.nailsInput}>
                      <span style={S.ctrlLabel}>Diámetro</span>
                      <input type="number" min={100} max={2000} value={frameDiameter}
                        onChange={(e) => setFrameDiameter(Math.max(100, Math.min(2000, Number(e.target.value))))}
                        style={{ ...S.nailNum, width: 72 }} />
                      <span style={{ fontSize: 11, color: ETIQUETA }}>mm</span>
                    </div>
                    <div style={S.nailsInput}>
                      <span style={S.ctrlLabel}>Hilo</span>
                      <input type="number" min={0.05} max={1} step={0.01} value={threadThickness}
                        onChange={(e) => setThreadThickness(Math.max(0.05, Math.min(1, Number(e.target.value))))}
                        style={{ ...S.nailNum, width: 72 }} />
                      <span style={{ fontSize: 11, color: ETIQUETA }}>mm</span>
                    </div>
                  </div>
                  {seleccionada && (() => {
                    const numStr = seleccionada.sequence.length - 1;
                    const lengthM = (numStr * frameDiameter * Math.PI / nailCount / 1000).toFixed(1);
                    return (
                      <div style={{
                        display: "flex", flexWrap: "wrap", gap: 10, justifyContent: "center",
                        fontFamily: MONO, fontSize: 11, color: "#93c5fd", textAlign: "center",
                        background: "rgba(37,99,235,0.1)", border: "1px solid rgba(59,130,246,0.25)",
                        borderRadius: 10, padding: "10px 14px", width: "100%",
                      }}>
                        <span>Hilo necesario <b style={{ color: "#fff" }}>{lengthM} m</b></span>
                        <span style={{ color: ETIQUETA }}>·</span>
                        <span>{numStr} líneas</span>
                        <span style={{ color: ETIQUETA }}>·</span>
                        <span>{nailCount} clavos</span>
                      </div>
                    );
                  })()}
                  {seleccionada && (
                    <div style={{ display: "flex", gap: 8, justifyContent: "center", flexWrap: "wrap" }}>
                      <button className="btn-sec" style={S.ctrlBtn}
                        onClick={() => {
                          const seq = Array.from(seleccionada.sequence);
                          navigator.clipboard.writeText(seq.join(",")).then(() => addLog("ok", `Secuencia copiada (${seq.length} valores)`));
                        }}>
                        <Copy size={15} /> Copiar
                      </button>
                      <button className="btn-sec" style={S.ctrlBtn} onClick={handleDownloadSequence}>
                        <Download size={15} /> Secuencia
                      </button>
                      <button className="btn-sec" style={S.ctrlBtn} onClick={handleDownloadImage}>
                        <ImageIcon size={15} /> PNG
                      </button>
                      <button className="btn-sec" style={S.ctrlBtn} onClick={handleDownloadJSON}>
                        <FileJson size={15} /> JSON
                      </button>
                    </div>
                  )}
                </div>

                <LogBox logs={logs} logRef={logRef} />
              </div>
            )}
          </div>
        )}
      </div>

      {/* NOMBRE DE LA SECUENCIA (antes de guardar) */}
      {pidiendoNombre && (
        <div style={S.modalFondo} onClick={() => { if (!guardando) setPidiendoNombre(false); }}>
          <div style={S.modalCaja} onClick={(e) => e.stopPropagation()}>
            <div style={{ color: "#fff", fontWeight: 700, fontSize: 15 }}>Ponle nombre a la secuencia</div>
            <div style={{ ...S.hint, textAlign: "left" }}>Así la reconocerás en tu lista de secuencias.</div>
            <input
              autoFocus
              value={nombreSecuencia}
              maxLength={60}
              placeholder="Ej. Retrato de Ana"
              onChange={(e) => setNombreSecuencia(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") guardarConNombre(nombreSecuencia); }}
              style={S.modalInput}
            />
            {generandoVista && (
              <div style={{ ...S.hint, textAlign: "left", color: AZUL_CLARO }}>
                Las demás variantes siguen calculándose. Al guardar se descartan.
              </div>
            )}
            {errorUso && (
              <div style={{
                fontSize: 12, color: "#fca5a5",
                background: "rgba(239,68,68,0.1)", border: "1px solid rgba(248,113,113,0.3)",
                borderRadius: 10, padding: "8px 12px",
              }}>{errorUso}</div>
            )}
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", flexWrap: "wrap" }}>
              <button className="btn-sec" style={S.ctrlBtn} disabled={guardando}
                onClick={() => setPidiendoNombre(false)}>
                Cancelar
              </button>
              <button className="btn-primary" style={S.ctrlBtn}
                disabled={guardando || !nombreSecuencia.trim()}
                onClick={() => guardarConNombre(nombreSecuencia)}>
                {guardando
                  ? <><span style={S.spinner} /> Guardando…</>
                  : <><Check size={16} /> Guardar</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── ROTATION DIAL ─────────────────────────────────────────────────────────────

function RotationDial({ rotation, wrapSize, onRotate }) {
  const cx = wrapSize / 2;
  const cy = wrapSize / 2;
  const ringR = wrapSize / 2 - 10;
  const dragging = useRef(false);
  const svgRef = useRef(null);

  // 0° = top (12 o'clock), clockwise
  const toXY = (deg) => {
    const rad = (deg - 90) * Math.PI / 180;
    return { x: cx + ringR * Math.cos(rad), y: cy + ringR * Math.sin(rad) };
  };

  const angleFromEvent = (e) => {
    const rect = svgRef.current.getBoundingClientRect();
    const punto = e.touches?.[0] ?? e.changedTouches?.[0] ?? e;
    const mx = punto.clientX - rect.left - cx;
    const my = punto.clientY - rect.top - cy;
    let deg = Math.atan2(my, mx) * 180 / Math.PI + 90;
    if (deg < 0) deg += 360;
    return deg % 360;
  };

  useEffect(() => {
    const move = (e) => {
      if (!dragging.current) return;
      if (e.touches) e.preventDefault();
      onRotate(angleFromEvent(e));
    };
    const up   = ()  => { dragging.current = false; };
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
    window.addEventListener("touchmove", move, { passive: false });
    window.addEventListener("touchend", up);
    return () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
      window.removeEventListener("touchmove", move);
      window.removeEventListener("touchend", up);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onRotate]);

  const { x: hx, y: hy } = toXY(rotation);
  // North marker
  const { x: nx, y: ny } = toXY(0);

  return (
    <svg ref={svgRef} width={wrapSize} height={wrapSize}
      style={{ position: "absolute", top: 0, left: 0, pointerEvents: "none" }}>
      {/* Dashed orbit ring */}
      <circle cx={cx} cy={cy} r={ringR} fill="none" stroke="rgba(51,65,85,0.7)" strokeWidth={1} strokeDasharray="4 6" />
      {/* North marker — fixed triangle pointing inward */}
      <polygon points={`${nx},${ny} ${nx-5},${ny+10} ${nx+5},${ny+10}`} fill="#475569" />
      {/* Line from center to handle */}
      <line x1={cx} y1={cy} x2={hx} y2={hy} stroke="rgba(51,65,85,0.6)" strokeWidth={0.8} />
      {/* Handle */}
      <g style={{ pointerEvents: "all", cursor: "grab", touchAction: "none" }}
        onMouseDown={(e) => { e.preventDefault(); dragging.current = true; }}
        onTouchStart={(e) => { e.stopPropagation(); dragging.current = true; }}>
        <circle cx={hx} cy={hy} r={22} fill="transparent" stroke="none" />
        <circle cx={hx} cy={hy} r={16} fill="#1e293b" stroke={AZUL} strokeWidth={1.5} />
        <text x={hx} y={hy} textAnchor="middle" dominantBaseline="middle"
          fontSize={9} fontWeight={700} fill={AZUL_CLARO} fontFamily={MONO} style={{ pointerEvents: "none", userSelect: "none" }}>
          {Math.round(rotation)}°
        </text>
      </g>
    </svg>
  );
}

// ─── LOG BOX ──────────────────────────────────────────────────────────────────

function LogBox({ logs, logRef }) {
  return (
    <div style={{ ...S.card, gap: 8, paddingBottom: 12 }}>
      <div style={S.cardLabel}>Actividad</div>
      <div ref={logRef} style={{
        maxHeight: 96, overflowY: "auto",
        background: "rgba(2,6,23,0.5)", borderRadius: 8,
        border: "1px solid " + BORDE, padding: "8px 10px",
      }}>
        {logs.slice(-20).map((l, i) => (
          <div key={i} style={{ color: LOG_COLOR[l.type] || ETIQUETA, fontFamily: MONO, fontSize: 10, lineHeight: 1.8 }}>
            <span style={{ color: "#334155" }}>&gt;</span> {l.msg}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── SMALL COMPONENTS ─────────────────────────────────────────────────────────


function Slider({ label, unit, min, max, value, onChange }) {
  return (
    <div style={{ width: "100%" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 7 }}>
        <span style={{ fontSize: 12, color: TENUE }}>{label}</span>
        <span style={{ fontFamily: MONO, fontSize: 12, fontWeight: 700, color: AZUL_CLARO }}>{value}{unit}</span>
      </div>
      <input type="range" min={min} max={max} value={value}
        onChange={(e) => onChange(Number(e.target.value))} />
    </div>
  );
}

// Campo + deslizador de clavos: se repite en las fases 1 y 2.
function ClavosControl({ nailCount, setNailCount }) {
  return (
    <div style={{ width: "100%" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 10 }}>
        <span style={S.ctrlLabel}>Clavos</span>
        <input type="number" min={CLAVOS_MIN} max={CLAVOS_MAX} value={nailCount}
          onChange={(e) => setNailCount(e.target.value)}
          style={{ ...S.nailNum, ...(nailCount !== CLAVOS_ESTANDAR ? { color: "#fbbf24", borderColor: "rgba(251,191,36,0.5)" } : {}) }} />
      </div>
      <input type="range" min={CLAVOS_MIN} max={CLAVOS_MAX} value={nailCount}
        onChange={(e) => setNailCount(e.target.value)} />
    </div>
  );
}


const CanvasCard = forwardRef(({ label, size }, ref) => (
  <div style={{ textAlign: "center" }}>
    <canvas ref={ref} width={200} height={200}
      style={{ width: size, height: size, borderRadius: "50%", border: "1px solid rgba(51,65,85,0.6)", display: "block" }} />
    <div style={{ fontSize: 11, color: ETIQUETA, marginTop: 8 }}>
      {label}
    </div>
  </div>
));

// ─── STYLES ───────────────────────────────────────────────────────────────────

// Paleta tomada de la app: slate-800/60 sobre slate-900, acento azul,
// tarjetas slate-900/50 con borde slate-700/40 y esquinas rounded-xl.
const AZUL       = "#3b82f6";
const AZUL_CLARO = "#60a5fa";
const TEXTO      = "#e2e8f0";  // slate-200
const TENUE      = "#94a3b8";  // slate-400
const ETIQUETA   = "#64748b";  // slate-500
const BORDE      = "rgba(51,65,85,0.4)";
const TARJETA    = "rgba(15,23,42,0.5)";
const MONO       = "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace";
const SANS       = '"Space Grotesk", "Inter", system-ui, sans-serif';

const S = {
  etiquetaNube: {
    display: "inline-flex", alignItems: "center", gap: 4, marginLeft: 8, padding: "2px 8px",
    borderRadius: 999, fontSize: 11, fontWeight: 700, color: AZUL_CLARO,
    background: "rgba(37,99,235,0.15)", border: "1px solid rgba(59,130,246,0.4)",
  },
  avisoMaxima: {
    position: "absolute", left: "50%", bottom: "8%", transform: "translateX(-50%)",
    display: "flex", alignItems: "center", gap: 6, whiteSpace: "nowrap",
    padding: "4px 10px", borderRadius: 999, fontSize: 11, fontWeight: 700, color: "#fff",
    background: "rgba(15,23,42,0.8)", border: "1px solid rgba(59,130,246,0.5)",
  },
  root: {
    display: "flex", flexDirection: "column",
    width: "100%", height: "100%", minHeight: 0,
    background: "rgba(30,41,59,0.6)",
    backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)",
    border: "1px solid rgba(51,65,85,0.5)",
    borderRadius: 12, overflow: "hidden",
    color: TEXTO, fontFamily: SANS,
  },
  header: {
    display: "flex", alignItems: "center", justifyContent: "space-between",
    gap: 12, flexWrap: "wrap",
    borderBottom: "1px solid " + BORDE,
    background: "rgba(15,23,42,0.6)", flexShrink: 0,
  },
  titulo: {
    display: "flex", alignItems: "center", gap: 10,
    fontSize: 18, fontWeight: 300, color: "#fff", whiteSpace: "nowrap",
  },
  // Pasos del asistente, con el mismo lenguaje que las pestañas de la app.
  pill: {
    display: "flex", alignItems: "center", gap: 6,
    padding: "5px 12px", borderRadius: 10,
    fontSize: 10, fontWeight: 700, letterSpacing: "0.08em",
    textTransform: "uppercase",
    border: "1px solid " + BORDE, color: ETIQUETA,
    background: "rgba(30,41,59,0.5)",
  },
  pillActive: {
    background: "linear-gradient(to right, #2563eb, #3b82f6)",
    color: "#fff", border: "1px solid rgba(96,165,250,0.4)",
    boxShadow: "0 4px 14px rgba(37,99,235,0.3)",
  },
  pillNum: {
    fontFamily: MONO, fontSize: 9, opacity: 0.7,
  },
  main: {
    flex: 1, display: "flex", flexDirection: "column",
    alignItems: "center", justifyContent: "safe center",
    gap: 0, minHeight: 0,
    overflowY: "auto", overflowX: "hidden",
    WebkitOverflowScrolling: "touch",
  },
  phase: {
    display: "flex", flexDirection: "column",
    alignItems: "center", gap: 18,
    width: "100%", flexShrink: 0,
  },
  // Encabezado de cada paso: número en círculo + título + explicación breve.
  stepTitle: {
    display: "flex", alignItems: "flex-start", gap: 12,
    width: "100%", maxWidth: 520,
  },
  stepNum: {
    width: 28, height: 28, borderRadius: "50%", flexShrink: 0,
    background: "linear-gradient(to right, #2563eb, #3b82f6)",
    color: "#fff", fontWeight: 700, fontSize: 13,
    display: "flex", alignItems: "center", justifyContent: "center",
    boxShadow: "0 4px 14px rgba(37,99,235,0.3)",
  },
  hint: {
    fontSize: 12, color: TENUE, textAlign: "center", lineHeight: 1.6,
  },
  // Tarjeta: el contenedor base de toda la app (slate-900/50 + borde).
  card: {
    width: "100%", maxWidth: 520,
    background: TARJETA, border: "1px solid " + BORDE,
    borderRadius: 12, padding: 16,
    display: "flex", flexDirection: "column", gap: 12,
  },
  cardLabel: {
    fontSize: 10, color: ETIQUETA, fontWeight: 700,
    letterSpacing: "0.2em", textTransform: "uppercase",
  },
  bigUpload: {
    display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 16,
    flexShrink: 0, marginTop: "auto", marginBottom: "auto", borderRadius: "50%",
    border: "2px dashed rgba(71,85,105,0.7)", cursor: "pointer",
    background: "rgba(15,23,42,0.5)",
    transition: "border-color 0.2s, background 0.2s",
  },
  modalFondo: {
    position: "fixed", inset: 0, zIndex: 60,
    background: "rgba(2,6,23,0.75)", backdropFilter: "blur(2px)",
    display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
  },
  modalCaja: {
    width: "100%", maxWidth: 400,
    background: "#0f172a", border: "1px solid " + BORDE,
    borderRadius: 16, padding: 20,
    display: "flex", flexDirection: "column", gap: 12,
    boxShadow: "0 20px 50px rgba(0,0,0,0.5)",
  },
  modalInput: {
    width: "100%", boxSizing: "border-box",
    background: "rgba(30,41,59,0.8)", border: "1px solid " + BORDE,
    borderRadius: 10, padding: "12px 14px",
    color: "#fff", fontSize: 15, outline: "none",
  },
  // Tarjeta del QR junto a la zona de carga.
  qrCard: {
    display: "flex", flexDirection: "column", alignItems: "center", gap: 12,
    background: TARJETA, border: "1px solid " + BORDE, borderRadius: 16, padding: 16,
    flexShrink: 0, textAlign: "center",
  },
  smallUpload: {
    display: "inline-flex", alignItems: "center", gap: 8,
    fontSize: 13, fontWeight: 700, color: "#cbd5e1",
    background: "rgba(30,41,59,0.7)", border: "1px solid " + BORDE,
    borderRadius: 12, padding: "10px 16px", minHeight: 40,
    cursor: "pointer", whiteSpace: "nowrap",
    transition: "color 0.2s, background 0.2s",
  },
  controls: {
    display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center", justifyContent: "center",
    maxWidth: 520, width: "100%",
  },
  nailsInput: {
    display: "flex", alignItems: "center", gap: 10,
  },
  ctrlLabel: {
    fontSize: 10, color: ETIQUETA, fontWeight: 700,
    letterSpacing: "0.2em", textTransform: "uppercase",
  },
  nailNum: {
    width: 68, background: "#1e293b",
    borderWidth: 1, borderStyle: "solid", borderColor: "#475569",
    borderRadius: 8, color: AZUL_CLARO, fontFamily: MONO,
    fontSize: 15, fontWeight: 700, padding: "6px 8px",
    textAlign: "center", outline: "none",
  },
  ctrlBtn: {
    display: "inline-flex", alignItems: "center", gap: 8,
    fontSize: 13, fontWeight: 700, color: "#cbd5e1",
    background: "rgba(30,41,59,0.7)", border: "1px solid " + BORDE,
    borderRadius: 12, padding: "10px 16px", minHeight: 40,
    cursor: "pointer", whiteSpace: "nowrap",
    transition: "color 0.2s, background 0.2s",
  },
  mainBtn: {
    padding: "12px 28px", minHeight: 46, maxWidth: "100%",
    background: "linear-gradient(to right, #2563eb, #3b82f6)",
    color: "#fff", border: "none", borderRadius: 12,
    fontFamily: SANS, fontSize: 14, fontWeight: 700,
    cursor: "pointer", letterSpacing: "-0.01em",
    boxShadow: "0 10px 15px -3px rgba(30,58,138,0.35)",
    display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
  },
  backBtn: {
    alignSelf: "flex-start",
    display: "inline-flex", alignItems: "center", gap: 8,
    background: "transparent", border: "1px solid " + BORDE, borderRadius: 12,
    padding: "8px 14px", fontSize: 12, fontWeight: 700,
    color: TENUE, cursor: "pointer",
  },
  chip: {
    display: "flex", alignItems: "center", gap: 7,
    background: "rgba(30,41,59,0.6)", border: "1px solid " + BORDE,
    borderRadius: 999, padding: "5px 12px",
  },
  estadoLista: {
    display: "flex", flexWrap: "wrap", gap: 6,
    alignItems: "center", justifyContent: "center",
    width: "100%", maxWidth: 520,
  },
  estadoChip: {
    display: "inline-flex", alignItems: "center", gap: 6,
    padding: "6px 10px", minHeight: 30, borderRadius: 999,
    background: "rgba(30,41,59,0.7)", border: "1px solid " + BORDE,
    color: "#cbd5e1", fontFamily: SANS, fontSize: 11, fontWeight: 700,
    cursor: "pointer", whiteSpace: "nowrap", transition: "background 0.2s, color 0.2s",
  },
  estadoChipSel: {
    background: "linear-gradient(to right, #2563eb, #3b82f6)",
    color: "#fff", border: "1px solid rgba(96,165,250,0.5)",
    boxShadow: "0 4px 14px rgba(37,99,235,0.3)",
  },
  arrowBtn: {
    width: 40, height: 40, borderRadius: "50%",
    border: "1px solid " + BORDE, background: "rgba(30,41,59,0.7)",
    color: "#cbd5e1", fontSize: 20, cursor: "pointer",
    display: "flex", alignItems: "center", justifyContent: "center",
    flexShrink: 0, padding: 0, lineHeight: 1,
  },
  spinner: {
    width: 14, height: 14, border: "2px solid rgba(255,255,255,0.35)",
    borderTopColor: "#fff", borderRadius: "50%",
    display: "inline-block", animation: "saSpin 0.7s linear infinite",
  },
};

const GLOBAL_CSS = `
  @keyframes saSpin { to { transform: rotate(360deg); } }
  @keyframes saPulse { 0%,100%{opacity:1} 50%{opacity:0.4} }
  .sa-studio .sa-espera { animation: saPulse 1.4s ease-in-out infinite; color: #fbbf24; }
  .sa-studio *, .sa-studio *::before, .sa-studio *::after { box-sizing: border-box; }
  .sa-studio input[type=range] {
    -webkit-appearance: none; appearance: none;
    width: 100%; max-width: 100%; height: 6px;
    border-radius: 999px; background: #475569; outline: none; cursor: pointer;
  }
  .sa-studio input[type=range]::-webkit-slider-thumb {
    -webkit-appearance: none; appearance: none;
    width: 16px; height: 16px; border-radius: 50%;
    background: #3b82f6; border: 2px solid #bfdbfe; cursor: pointer;
  }
  .sa-studio input[type=range]::-moz-range-thumb {
    width: 16px; height: 16px; border-radius: 50%; border: 2px solid #bfdbfe;
    background: #3b82f6; cursor: pointer;
  }
  .sa-studio input[type=range]:disabled { opacity: 0.4; cursor: not-allowed; }
  .sa-studio input[type=number]:focus { border-color: #3b82f6; }
  .sa-studio ::-webkit-scrollbar { width: 6px; height: 6px; }
  .sa-studio ::-webkit-scrollbar-track { background: transparent; }
  .sa-studio ::-webkit-scrollbar-thumb { background: #475569; border-radius: 999px; }
  .sa-studio ::-webkit-scrollbar-thumb:hover { background: #64748b; }
  .sa-studio .upload-zone:hover { border-color: #3b82f6 !important; background: rgba(30,41,59,0.6) !important; }
  .sa-studio .btn-sec:hover:not(:disabled) { color: #fff !important; background: rgba(51,65,85,0.7) !important; }
  .sa-studio .btn-primary:hover:not(:disabled) { filter: brightness(1.08); }
  .sa-studio .btn-primary:disabled { opacity: 0.4; cursor: not-allowed; box-shadow: none; }
`;
